import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Calendar, Clock, MagnifyingGlass, Funnel, ShieldCheck, SpeakerHigh, X, NavigationArrow, Crosshair, MapTrifold, ListBullets, Globe, Television, Ticket, ShareNetwork, ArrowLeft, Link as LinkIcon, Megaphone, ArrowRight, Star, Heart, Flag, CheckCircle } from '@phosphor-icons/react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Helmet } from 'react-helmet-async';
import { NewsletterInline } from '../components/NewsletterPrompts';
import { getStaticMapUrl } from '../components/AddressAutocomplete';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EVENT_TYPES = ['Viewing Party', 'After-party', 'Cruise', 'Festival', 'Official Festival', 'Restaurant', 'Cafe', 'Sports Club', 'Sport Event', 'Special Event', 'Other'];
const VIBE_OPTIONS = ['Beer Garden', 'Family Friendly', 'Full Menu', 'Cocktail Lounge', 'Halal Menu', 'Food Trucks', 'Tap Specials', 'Free Wifi', 'Dog Friendly', 'Supporters Group', 'BYOB', 'BYOW', 'Vegan/Vegetarian', 'Authentic', 'Latin Vibes', 'Live Music', '19+', 'Scenic View', 'No Alcohol', 'Family Zone', 'High Energy', 'Indoor/Outdoor', 'Inclusive', 'Vibrant', 'Street Party', 'Giveaways', 'Fan Zone', 'Immersive', 'Local', 'Independent', 'Culture', 'Date Night'];
const ENTRY_TYPES = [
  { value: 'open_event', label: 'Open' },
  { value: 'free_event', label: 'Free' },
  { value: 'no_cover', label: 'No Cover' },
  { value: 'ticketed', label: 'Ticketed' },
  { value: 'bottle_service', label: 'VIP / Bottle' },
];

// City coordinate lookup for map pins
const CITY_COORDS = {
  'Toronto': [43.6532, -79.3832],
  'Vancouver': [49.2827, -123.1207],
  'Montreal': [45.5017, -73.5673],
  'New York': [40.7128, -74.0060],
  'Los Angeles': [34.0522, -118.2437],
  'Miami': [25.7617, -80.1918],
  'Mexico City': [19.4326, -99.1332],
  'Guadalajara': [20.6597, -103.3496],
  'Monterrey': [25.6866, -100.3161],
  'Calgary': [51.0447, -114.0719],
  'Ottawa': [45.4215, -75.6972],
  'Chicago': [41.8781, -87.6298],
  'Houston': [29.7604, -95.3698],
  'Dallas': [32.7767, -96.7970],
  'Seattle': [47.6062, -122.3321],
  'Boston': [42.3601, -71.0589],
  'Philadelphia': [39.9526, -75.1652],
  'Atlanta': [33.7490, -84.3880],
  'San Francisco': [37.7749, -122.4194],
  'Mississauga': [43.5890, -79.6441],
  'East Rutherford': [40.8337, -74.0973],
  'Coral Gables': [25.7215, -80.2684],
  'Sugar Land': [29.6197, -95.6349],
  'Zapopan': [20.7230, -103.3844],
  'CDMX': [19.4326, -99.1332],
};
const DEFAULT_CENTER = [39.8283, -98.5795]; // Center of North America

// Bird's eye: Country centers for overview zoom
const COUNTRY_VIEWS = {
  'Canada': { center: [56.1304, -106.3468], zoom: 4 },
  'USA': { center: [39.8283, -98.5795], zoom: 4 },
  'Mexico': { center: [23.6345, -102.5528], zoom: 5 },
};

const isMobileOrTablet = () => window.innerWidth < 1024;

// World Soccer 2026 kickoff (opening match: June 11, 2026 — Estadio Azteca, Mexico City)
const KICKOFF_DATE = new Date('2026-06-11T16:00:00-06:00');

// A placeholder listing = a curated venue slot that has not yet been claimed by a paying owner
// (no invoice attached and not an official FIFA-licensed event).
const isPlaceholderListing = (listing) => {
  if (!listing) return false;
  if (listing.fifa_license) return false;
  if (listing.invoice_number) return false;
  // Hide on internal World Soccer Party owned events (already curated, no claim)
  if ((listing.event_name || '').startsWith('World Soccer')) return false;
  return true;
};

const getDaysToKickoff = () => {
  const ms = KICKOFF_DATE.getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
};

// Country → flag emoji + display name helpers for the Team field on Fan Cards.
// Returns null if value is missing, "N/A", "Any", "Other", etc.
// FIFA-style 3-letter country codes → { flag, name } so listings with
// "ARG"/"MEX"/"BRA" render with the proper flag instead of an opaque acronym.
const TEAM_CODES = {
  can: { flag: '🇨🇦', name: 'Canada' }, usa: { flag: '🇺🇸', name: 'USA' }, mex: { flag: '🇲🇽', name: 'Mexico' },
  bra: { flag: '🇧🇷', name: 'Brazil' }, arg: { flag: '🇦🇷', name: 'Argentina' }, ger: { flag: '🇩🇪', name: 'Germany' },
  esp: { flag: '🇪🇸', name: 'Spain' }, fra: { flag: '🇫🇷', name: 'France' }, eng: { flag: '🏴󠁧󠁢󠁥󠁮󠁧󠁿', name: 'England' },
  gbr: { flag: '🇬🇧', name: 'UK' }, ita: { flag: '🇮🇹', name: 'Italy' }, por: { flag: '🇵🇹', name: 'Portugal' },
  ned: { flag: '🇳🇱', name: 'Netherlands' }, bel: { flag: '🇧🇪', name: 'Belgium' }, cro: { flag: '🇭🇷', name: 'Croatia' },
  sui: { flag: '🇨🇭', name: 'Switzerland' }, den: { flag: '🇩🇰', name: 'Denmark' }, swe: { flag: '🇸🇪', name: 'Sweden' },
  nor: { flag: '🇳🇴', name: 'Norway' }, pol: { flag: '🇵🇱', name: 'Poland' }, tur: { flag: '🇹🇷', name: 'Turkey' },
  mar: { flag: '🇲🇦', name: 'Morocco' }, sen: { flag: '🇸🇳', name: 'Senegal' }, nga: { flag: '🇳🇬', name: 'Nigeria' },
  gha: { flag: '🇬🇭', name: 'Ghana' }, cmr: { flag: '🇨🇲', name: 'Cameroon' }, egy: { flag: '🇪🇬', name: 'Egypt' },
  kor: { flag: '🇰🇷', name: 'South Korea' }, jpn: { flag: '🇯🇵', name: 'Japan' }, irn: { flag: '🇮🇷', name: 'Iran' },
  ksa: { flag: '🇸🇦', name: 'Saudi Arabia' }, aus: { flag: '🇦🇺', name: 'Australia' }, nzl: { flag: '🇳🇿', name: 'New Zealand' },
  chi: { flag: '🇨🇱', name: 'Chile' }, col: { flag: '🇨🇴', name: 'Colombia' }, per: { flag: '🇵🇪', name: 'Peru' },
  uru: { flag: '🇺🇾', name: 'Uruguay' }, ecu: { flag: '🇪🇨', name: 'Ecuador' }, par: { flag: '🇵🇾', name: 'Paraguay' },
  bol: { flag: '🇧🇴', name: 'Bolivia' }, ven: { flag: '🇻🇪', name: 'Venezuela' }, crc: { flag: '🇨🇷', name: 'Costa Rica' },
  pan: { flag: '🇵🇦', name: 'Panama' }, hon: { flag: '🇭🇳', name: 'Honduras' }, gua: { flag: '🇬🇹', name: 'Guatemala' },
  slv: { flag: '🇸🇻', name: 'El Salvador' }, jam: { flag: '🇯🇲', name: 'Jamaica' }, hai: { flag: '🇭🇹', name: 'Haiti' },
  cub: { flag: '🇨🇺', name: 'Cuba' }, srb: { flag: '🇷🇸', name: 'Serbia' }, cze: { flag: '🇨🇿', name: 'Czechia' },
  aut: { flag: '🇦🇹', name: 'Austria' }, hun: { flag: '🇭🇺', name: 'Hungary' }, rou: { flag: '🇷🇴', name: 'Romania' },
  gre: { flag: '🇬🇷', name: 'Greece' }, irl: { flag: '🇮🇪', name: 'Ireland' }, sco: { flag: '🏴󠁧󠁢󠁳󠁣󠁴󠁿', name: 'Scotland' },
  wal: { flag: '🏴󠁧󠁢󠁷󠁬󠁳󠁿', name: 'Wales' }, ind: { flag: '🇮🇳', name: 'India' }, qat: { flag: '🇶🇦', name: 'Qatar' },
  uae: { flag: '🇦🇪', name: 'UAE' }, isr: { flag: '🇮🇱', name: 'Israel' }, rus: { flag: '🇷🇺', name: 'Russia' },
  ukr: { flag: '🇺🇦', name: 'Ukraine' },
};

const TEAM_FLAGS = {
  'canada': '🇨🇦', 'usa': '🇺🇸', 'united states': '🇺🇸', 'mexico': '🇲🇽',
  'brazil': '🇧🇷', 'argentina': '🇦🇷', 'germany': '🇩🇪', 'spain': '🇪🇸',
  'france': '🇫🇷', 'england': '🏴󠁧󠁢󠁥󠁮󠁧󠁿', 'uk': '🇬🇧', 'italy': '🇮🇹',
  'portugal': '🇵🇹', 'netherlands': '🇳🇱', 'belgium': '🇧🇪', 'croatia': '🇭🇷',
  'switzerland': '🇨🇭', 'denmark': '🇩🇰', 'sweden': '🇸🇪', 'norway': '🇳🇴',
  'poland': '🇵🇱', 'turkey': '🇹🇷', 'morocco': '🇲🇦', 'senegal': '🇸🇳',
  'nigeria': '🇳🇬', 'ghana': '🇬🇭', 'cameroon': '🇨🇲', 'egypt': '🇪🇬',
  'south korea': '🇰🇷', 'korea': '🇰🇷', 'japan': '🇯🇵', 'iran': '🇮🇷',
  'saudi arabia': '🇸🇦', 'australia': '🇦🇺', 'new zealand': '🇳🇿',
  'chile': '🇨🇱', 'colombia': '🇨🇴', 'peru': '🇵🇪', 'uruguay': '🇺🇾',
  'ecuador': '🇪🇨', 'paraguay': '🇵🇾', 'bolivia': '🇧🇴', 'venezuela': '🇻🇪',
  'costa rica': '🇨🇷', 'panama': '🇵🇦', 'honduras': '🇭🇳', 'guatemala': '🇬🇹',
  'el salvador': '🇸🇻', 'jamaica': '🇯🇲', 'haiti': '🇭🇹', 'cuba': '🇨🇺',
  'serbia': '🇷🇸', 'czechia': '🇨🇿', 'czech republic': '🇨🇿', 'austria': '🇦🇹',
  'hungary': '🇭🇺', 'romania': '🇷🇴', 'greece': '🇬🇷', 'ireland': '🇮🇪',
  'scotland': '🏴󠁧󠁢󠁳󠁣󠁴󠁿', 'wales': '🏴󠁧󠁢󠁷󠁬󠁳󠁿', 'india': '🇮🇳', 'qatar': '🇶🇦', 'uae': '🇦🇪',
  'israel': '🇮🇱', 'russia': '🇷🇺', 'ukraine': '🇺🇦',
  'tunisia': '🇹🇳', 'algeria': '🇩🇿', 'south africa': '🇿🇦', 'ivory coast': '🇨🇮',
  'finland': '🇫🇮', 'iceland': '🇮🇸', 'slovakia': '🇸🇰', 'slovenia': '🇸🇮',
  'bosnia': '🇧🇦', 'north macedonia': '🇲🇰', 'albania': '🇦🇱',
  'china': '🇨🇳', 'thailand': '🇹🇭', 'vietnam': '🇻🇳', 'philippines': '🇵🇭',
  'indonesia': '🇮🇩', 'malaysia': '🇲🇾', 'singapore': '🇸🇬',
};
// Anything that comes in as garbage ("[]", "{}", "null", "any", "n/a", "-",
// punctuation-only, etc.) is treated as unselected and the tag is hidden.
const EXCLUDED_TEAM_VALUES = new Set(['', 'n/a', 'na', 'any', 'none', 'other', 'tbd', '-', '[]', '{}', 'null', 'undefined']);

// Normalise a single team value → { flag, name } or null if it's garbage.
const normaliseTeam = (rawVal) => {
  if (rawVal === null || rawVal === undefined) return null;
  let s = typeof rawVal === 'string' ? rawVal : String(rawVal);
  s = s.trim().replace(/^[\[\]{}"'`]+|[\[\]{}"'`]+$/g, '').trim();
  if (!s || EXCLUDED_TEAM_VALUES.has(s.toLowerCase())) return null;
  if (!/[a-z]/i.test(s)) return null; // pure punctuation
  const lower = s.toLowerCase();
  // 3-letter FIFA code (e.g., ARG, MEX, BRA)
  if (s.length === 3 && TEAM_CODES[lower]) return TEAM_CODES[lower];
  // Full country name
  if (TEAM_FLAGS[lower]) return { flag: TEAM_FLAGS[lower], name: s };
  // Unknown — display name without a flag (still better than acronym garbage)
  return { flag: '', name: s };
};

// Parse the raw team_specialization payload into an array of {flag,name} entries.
const parseTeamSpec = (raw) => {
  if (!raw) return [];
  // Array case
  if (Array.isArray(raw)) return raw.map(normaliseTeam).filter(Boolean);
  // Stringified JSON array case (e.g. "[]", "[\"ARG\"]")
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) return parsed.map(normaliseTeam).filter(Boolean);
      } catch { /* fall through to comma split */ }
    }
    return trimmed.split(',').map(normaliseTeam).filter(Boolean);
  }
  return [];
};

const renderTeamLabel = (raw) => {
  const items = parseTeamSpec(raw);
  if (items.length === 0) return null;
  // Render as flag + name pairs (drop the legacy "Team " prefix — flag IS the brand cue).
  return items.map(it => it.flag ? `${it.flag} ${it.name}` : it.name).join(' · ');
};

// Heart helper
const heartEvent = async (listing, setLocalHearts) => {
  try {
    const res = await axios.post(`${API}/venues/${listing.id}/heart`);
    if (setLocalHearts) setLocalHearts(res.data.heart_count);
  } catch {}
};

// Share helper
const shareEvent = async (listing, setLocalHearts) => {
  const url = `${window.location.origin}/listings?event=${listing.id}`;
  const text = `${listing.event_name} at ${listing.venue_name} — ${listing.city}, ${listing.country}`;
  let shareType = 'other';
  if (navigator.share) {
    try {
      await navigator.share({ title: listing.event_name, text, url });
      shareType = 'social';
    } catch { return; }
  } else {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied!');
    } catch { return; }
  }
  // Track share in backend (+2 hearts, +1 share)
  try {
    const res = await axios.post(`${API}/venues/${listing.id}/share`, { venue_id: listing.id, share_type: shareType });
    if (setLocalHearts) setLocalHearts(res.data.heart_count);
  } catch {}
};

// MapPanner is rendered inside MapContainer and uses react-leaflet's useMap
const MapPannerInner = ({ coords, zoomTarget, useMap, isDetailMode }) => {
  const map = useMap();
  useEffect(() => {
    if (zoomTarget) {
      map.flyTo(zoomTarget.center, zoomTarget.zoom, { animate: true, duration: 0.8 });
    } else if (coords) {
      map.panTo(coords, { animate: true, duration: 0.4 });
    }
  }, [coords, zoomTarget, map]);
  // When the container resizes (detail-mode toggle), Leaflet needs invalidateSize()
  // so the tile layer recalculates and re-paints. Without this the map appears blank.
  useEffect(() => {
    const id = setTimeout(() => { try { map.invalidateSize(); } catch {} }, 250);
    return () => clearTimeout(id);
  }, [isDetailMode, map]);
  return null;
};

// Mobile map: Browse mode (full map + carousel) and Detail mode (map top + full card bottom)
const MobileMapOverlay = ({ listings, onClose, userLocation }) => {
  const [mapLoaded, setMapLoaded] = useState(false);
  const [leafletModules, setLeafletModules] = useState(null);
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [panTarget, setPanTarget] = useState(null);
  const [zoomTarget, setZoomTarget] = useState(null);
  const [teamFilter, setTeamFilter] = useState('all');

  // Browser-back integration: pushing a marker entry into history so the device
  // back button can close the overlay/sheet. Cleanup only removes the listener
  // (calling history.back() in cleanup races StrictMode and breaks the map).
  useEffect(() => {
    if (window.history.state?.wspMap !== true) {
      window.history.pushState({ wspMap: true }, '');
    }
    const handlePopState = () => onClose();
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Local helpers (in-scope versions of getDistanceLabel/getMapsUrl)
  const getDist = (lat1, lng1, lat2, lng2) => {
    if (!lat1 || !lng1 || !lat2 || !lng2) return null;
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };
  const getDistanceLabel = (listing) => {
    if (!userLocation || !listing?.venue_lat || !listing?.venue_lng) return null;
    const km = getDist(userLocation.lat, userLocation.lng, listing.venue_lat, listing.venue_lng);
    if (km == null) return null;
    return km < 1 ? `${Math.round(km * 1000)}m away` : `${km.toFixed(1)}km away`;
  };
  const getMapsUrl = (listing) => {
    if (listing?.venue_lat && listing?.venue_lng) {
      return `https://www.google.com/maps/dir/?api=1&destination=${listing.venue_lat},${listing.venue_lng}`;
    }
    const q = encodeURIComponent(`${listing?.venue_name || ''} ${listing?.location_address || ''}`);
    return `https://www.google.com/maps/search/?api=1&query=${q}`;
  };

  const [allListingsWithCoords] = useState(() => {
    return listings.map(l => {
      // Prefer real geocoded coords saved during venue registration (Google Places API).
      // Falls back to city-center with a tiny random offset only when lat/lng are missing
      // (legacy listings that pre-date geocoding). This guarantees the pin matches the
      // actual address for every claim-verified venue.
      const lat = typeof l.latitude === 'number' ? l.latitude : (typeof l.lat === 'number' ? l.lat : null);
      const lng = typeof l.longitude === 'number' ? l.longitude : (typeof l.lng === 'number' ? l.lng : null);
      if (lat !== null && lng !== null && Math.abs(lat) > 0.1 && Math.abs(lng) > 0.1) {
        return { ...l, coords: [lat, lng], coords_source: 'geocoded' };
      }
      const city = l.city || '';
      if (CITY_COORDS[city]) {
        const offset = () => (Math.random() - 0.5) * 0.015;
        return { ...l, coords: [CITY_COORDS[city][0] + offset(), CITY_COORDS[city][1] + offset()], coords_source: 'city_fallback' };
      }
      return null;
    }).filter(Boolean);
  });

  // (Msg 778) Team filter: combine both legacy team_specialization AND the
  // new match_coverage_countries field. Sports venues now declare their
  // supported teams via the country chip selector at registration —
  // /events team filter must see them too.
  const collectListingTeams = (l) => {
    const out = new Map();
    parseTeamSpec(l.team_specialization).forEach(({ name, flag }) => {
      if (name && !out.has(name)) out.set(name, { label: name, flag });
    });
    const mcc = l.match_coverage_countries;
    if (Array.isArray(mcc)) {
      mcc.forEach(c => {
        const norm = normaliseTeam(c);
        if (norm && norm.name && !out.has(norm.name)) out.set(norm.name, { label: norm.name, flag: norm.flag });
      });
    }
    return [...out.values()];
  };

  // Team filter: extract unique normalised country names from team_specialization
  // and match_coverage_countries. Each option exposes { label, flag } so chips
  // render the flag instead of acronyms.
  const teamOptions = useMemo(() => {
    const map = new Map();
    allListingsWithCoords.forEach(l => {
      collectListingTeams(l).forEach(({ label, flag }) => {
        if (!map.has(label)) map.set(label, { label, flag });
      });
    });
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [allListingsWithCoords]);

  // Filter listings by selected team (normalised country name)
  const listingsWithCoords = useMemo(() => {
    if (teamFilter === 'all') return allListingsWithCoords;
    return allListingsWithCoords.filter(l => {
      const teams = collectListingTeams(l);
      return teams.some(t => t.label === teamFilter);
    });
  }, [allListingsWithCoords, teamFilter]);

  useEffect(() => {
    Promise.all([
      import('leaflet'),
      import('react-leaflet'),
      import('leaflet/dist/leaflet.css')
    ]).then(([L, RL]) => {
      setLeafletModules({ L: L.default, RL, useMap: RL.useMap });
      setMapLoaded(true);
    });
  }, []);

  const getBounds = () => {
    if (userLocation) {
      const r = 0.25;
      return [[userLocation.lat - r, userLocation.lng - r], [userLocation.lat + r, userLocation.lng + r]];
    }
    if (listingsWithCoords.length === 0) return [DEFAULT_CENTER, DEFAULT_CENTER];
    if (listingsWithCoords.length === 1) {
      const c = listingsWithCoords[0].coords;
      return [[c[0] - 2, c[1] - 2], [c[0] + 2, c[1] + 2]];
    }
    const lats = listingsWithCoords.map(l => l.coords[0]);
    const lngs = listingsWithCoords.map(l => l.coords[1]);
    return [[Math.min(...lats) - 0.5, Math.min(...lngs) - 0.5], [Math.max(...lats) + 0.5, Math.max(...lngs) + 0.5]];
  };

  // === Map interactions ===
  // selectEvent: zoom IN to a pin (level 14) + open the bottom sheet
  const selectEvent = (idx) => {
    const l = listingsWithCoords[idx];
    if (!l) return;
    setSelectedIdx(idx);
    setZoomTarget({ center: l.coords, zoom: 14 });
    setPanTarget(null);
  };
  const closeSheet = () => { setSelectedIdx(null); };

  const zoomToCountry = (country) => {
    const view = COUNTRY_VIEWS[country];
    if (view) {
      setSelectedIdx(null);
      setZoomTarget(view);
    }
  };

  // Count listings per country
  const countryCounts = {};
  listings.forEach(l => {
    const c = l.country || 'Other';
    countryCounts[c] = (countryCounts[c] || 0) + 1;
  });

  const createIcon = (sel, listing) => {
    if (!leafletModules) return null;
    const { L } = leafletModules;
    const isGolden = listing?.addon_golden_goal === true || listing?.fifa_license === true;
    if (isGolden) {
      const s = sel ? 36 : 28;
      return L.divIcon({
        className: 'golden-goal-pin',
        html: `<div style="width:${s}px;height:${s}px;position:relative;display:flex;align-items:center;justify-content:center;">
          <div style="position:absolute;inset:0;background:radial-gradient(circle,#FFD700,#FF8C00);border-radius:50%;animation:goldenPulse 1.5s ease-in-out infinite;box-shadow:0 0 12px rgba(255,215,0,0.6),0 2px 8px rgba(0,0,0,0.3);border:2.5px solid white;"></div>
          <svg style="position:relative;z-index:1;width:${Math.round(s*0.45)}px;height:${Math.round(s*0.45)}px;" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/><path d="M4 22h16"/><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/></svg>
        </div>`,
        iconSize: [s, s], iconAnchor: [s / 2, s / 2],
      });
    }
    const s = sel ? 22 : 16;
    return L.divIcon({
      className: 'custom-pin',
      html: `<div style="width:${s}px;height:${s}px;background:${sel ? '#FF6B35' : '#1a1a1a'};border:3px solid white;border-radius:50%;box-shadow:0 2px 8px rgba(0,0,0,${sel ? '0.4' : '0.25'});transition:all .2s;${sel ? 'transform:scale(1.3);' : ''}"></div>`,
      iconSize: [s, s], iconAnchor: [s / 2, s / 2],
    });
  };

  const entryLabel = (t) => ({ free_event: 'FREE', open_event: 'OPEN', ticketed: 'TICKETS', bottle_service: 'VIP', no_cover: 'NO COVER' }[t] || (t ? t.replace('_', ' ').toUpperCase() : ''));

  const sel = selectedIdx !== null ? listingsWithCoords[selectedIdx] : null;
  const vibes = sel?.vibe_tags
    ? (Array.isArray(sel.vibe_tags) ? sel.vibe_tags : String(sel.vibe_tags).split(',')).map(t => (t || '').toString().trim()).filter(Boolean)
    : [];
  const audioLbl = sel ? ({ full_commentary: 'Live Commentary', background_music: 'Music Only', silent: 'Silent/Subtitles' }[sel.audio_quality] || '') : '';

  const renderMap = () => {
    if (!mapLoaded || !leafletModules) return <div className="absolute inset-0 flex items-center justify-center bg-gray-50"><div className="w-8 h-8 border-3 border-[#FF6B35] border-t-transparent rounded-full animate-spin" /></div>;
    const { RL } = leafletModules;
    return (
      <RL.MapContainer bounds={getBounds()} boundsOptions={{ padding: [30, 30] }} style={{ height: '100%', width: '100%' }} zoomControl={true} attributionControl={false}>
        <RL.TileLayer url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png" />
        {listingsWithCoords.map((l, i) => (
          <RL.Marker key={l.id || i} position={l.coords} icon={createIcon(selectedIdx === i, l)} eventHandlers={{ click: () => selectEvent(i) }} zIndexOffset={selectedIdx === i ? 1000 : ((l.addon_golden_goal || l.fifa_license) ? 500 : 0)} />
        ))}
        <MapPannerInner coords={panTarget} zoomTarget={zoomTarget} useMap={leafletModules.useMap} isDetailMode={selectedIdx !== null} />
        {userLocation && (() => {
          const icon = leafletModules.L.divIcon({ className: 'u-loc', html: '<div style="width:14px;height:14px;background:#3B82F6;border:3px solid white;border-radius:50%;box-shadow:0 0 0 6px rgba(59,130,246,0.2),0 2px 6px rgba(0,0,0,0.2)"></div>', iconSize: [14, 14], iconAnchor: [7, 7] });
          return <RL.Marker position={[userLocation.lat, userLocation.lng]} icon={icon} zIndexOffset={2000} />;
        })()}
      </RL.MapContainer>
    );
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-white" data-testid="mobile-map-overlay">
      {/* === Always full-screen map === */}
      <div className="absolute inset-0" data-testid="map-pane">{renderMap()}</div>

      {/* === Top header bar (always-visible, floats on top of map) === */}
      <div className="absolute top-0 left-0 right-0 z-[1100] pointer-events-none">
        <div className="bg-gradient-to-b from-white via-white/90 to-transparent pb-4 pt-3 px-3 pointer-events-auto">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <MapTrifold size={20} weight="bold" className="text-[#FF6B35] flex-shrink-0" />
              <span className="font-['Bebas_Neue'] text-lg uppercase truncate">Event Map</span>
              <span className="text-xs text-gray-500 font-semibold flex-shrink-0">{listingsWithCoords.length}</span>
            </div>
            <button
              onClick={onClose}
              className="flex items-center gap-1.5 bg-gray-900 hover:bg-gray-800 text-white px-3 py-2 rounded-full text-xs font-bold shadow-md flex-shrink-0"
              data-testid="map-close-btn"
            >
              <ListBullets size={14} weight="bold" /> List
            </button>
          </div>

          {/* Country chips — flag-styled pills (Canada/USA/Mexico host nations), tap to zoom-fit */}
          <div className="flex gap-2 mt-3 overflow-x-auto" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }} data-testid="country-chips">
            {[
              { name: 'Canada', flag: '🇨🇦', primary: '#FF0000', secondary: '#FFFFFF' },
              { name: 'USA',    flag: '🇺🇸', primary: '#3C3B6E', secondary: '#B22234' },
              { name: 'Mexico', flag: '🇲🇽', primary: '#006847', secondary: '#CE1126' },
            ].map(({ name: country, flag, primary, secondary }) => (
              <button
                key={country}
                onClick={() => zoomToCountry(country)}
                className="flex items-center gap-2 bg-white/95 backdrop-blur-sm rounded-full shadow-md px-3 py-2 active:scale-95 transition-transform border-2 whitespace-nowrap"
                style={{
                  borderImage: `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%) 1`,
                  borderColor: primary,
                }}
                data-testid={`country-chip-${country.toLowerCase()}`}
                aria-label={`Show ${country} venues`}
              >
                <span className="text-base leading-none" aria-hidden="true">{flag}</span>
                <span className="text-xs font-extrabold tracking-wide" style={{ color: primary }}>{country}</span>
                <span
                  className="text-[10px] font-extrabold px-1.5 py-0.5 rounded-full text-white"
                  style={{ background: `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)` }}
                >
                  {countryCounts[country] || 0}
                </span>
              </button>
            ))}
            {teamOptions.length > 0 && (
              <>
                <div className="w-px bg-gray-300 mx-1 flex-shrink-0" />
                <button
                  onClick={() => setTeamFilter('all')}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-bold shadow-md whitespace-nowrap ${teamFilter === 'all' ? 'bg-[#FF6B35] text-white' : 'bg-white/95 text-gray-700 border border-gray-200'}`}
                  data-testid="team-filter-all"
                >
                  All Teams
                </button>
                {teamOptions.map(team => {
                  const teamSlug = team.label.replace(/\s+/g, '-').toLowerCase();
                  const active = teamFilter === team.label;
                  return (
                    <button
                      key={team.label}
                      onClick={() => setTeamFilter(team.label)}
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-bold shadow-md whitespace-nowrap ${active ? 'bg-[#FF6B35] text-white' : 'bg-white/95 text-gray-700 border border-gray-200'}`}
                      data-testid={`team-filter-${teamSlug}`}
                    >
                      {team.flag ? <span className="text-base leading-none">{team.flag}</span> : <Star size={11} weight="fill" className={active ? 'text-yellow-300' : 'text-amber-400'} />}
                      {team.label}
                    </button>
                  );
                })}
              </>
            )}
          </div>
        </div>
      </div>

      {/* === Hint pill when no pin selected === */}
      {selectedIdx === null && mapLoaded && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-[1050] bg-gray-900/85 backdrop-blur-sm text-white px-4 py-2 rounded-full text-xs font-semibold flex items-center gap-1.5 shadow-lg pointer-events-none" data-testid="map-hint">
          <Star size={11} weight="fill" className="text-amber-400" /> Tap a pin to zoom in
        </div>
      )}

      {/* === Bottom sheet (animated slide-up) === */}
      <AnimatePresence>
        {sel && (
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 280 }}
            className="absolute bottom-0 left-0 right-0 z-[1200] bg-white rounded-t-3xl shadow-2xl max-h-[72vh] flex flex-col"
            data-testid="map-detail-sheet"
          >
            {/* Sheet header */}
            <div className="flex items-center justify-between px-4 pt-3 pb-2 flex-shrink-0">
              <div className="w-12 h-1.5 bg-gray-300 rounded-full mx-auto" />
              <button
                onClick={closeSheet}
                className="absolute top-3 right-3 w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center"
                aria-label="Close event details"
                data-testid="sheet-close-btn"
              >
                <X size={18} weight="bold" />
              </button>
            </div>
            <div className="overflow-y-auto" data-testid="sheet-scroll-area">
              {sel.image_url ? <img src={sel.image_url} alt={sel.event_name} className="w-full h-44 object-cover" /> : <div className="w-full h-44 bg-gradient-to-br from-[#FF6B35] to-[#FF8C42] flex items-center justify-center"><span className="text-white font-bold text-lg opacity-50">{sel.event_type}</span></div>}
              <div className="p-5 space-y-4">
                <div>
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-['Bebas_Neue'] text-2xl uppercase text-gray-900 flex-1">{sel.event_name}</h3>
                    <div className="flex items-center gap-1.5 flex-shrink-0 mt-1">
                      {sel.fifa_license && <span className="text-xs font-bold text-blue-600 bg-blue-50 px-2 py-1 rounded-full flex items-center gap-1"><ShieldCheck size={11} weight="bold" />VERIFIED</span>}
                      {sel.pricing_package === 'premium' && <span className="text-xs font-bold text-white bg-gradient-to-r from-purple-500 to-pink-500 px-2 py-1 rounded-full">PREMIUM</span>}
                    </div>
                  </div>
                  <p className="text-sm text-[#FF6B35] font-bold uppercase tracking-wider mt-1">{sel.venue_name}</p>
                  <span className="inline-block mt-1 text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{sel.event_type}</span>
                </div>
                {sel.event_description && <p className="text-sm text-gray-700 leading-relaxed">{sel.event_description}</p>}
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-gray-700"><MapPin size={16} weight="bold" className="text-[#FF6B35] flex-shrink-0" /><span>{sel.location_address || `${sel.city}, ${sel.country}`}</span></div>
                  {getDistanceLabel(sel) && <div className="flex items-center gap-2 text-sm text-green-600 font-bold"><NavigationArrow size={14} weight="bold" /><span>{getDistanceLabel(sel)}</span></div>}
                  {sel.event_date && <div className="flex items-center gap-2 text-sm text-gray-700"><Calendar size={16} weight="bold" className="text-[#FF6B35] flex-shrink-0" /><span>{sel.event_date}</span></div>}
                  {sel.event_time && <div className="flex items-center gap-2 text-sm text-gray-700"><Clock size={16} weight="bold" className="text-[#FF6B35] flex-shrink-0" /><span>{sel.event_time}</span></div>}
                </div>
                {audioLbl && <div className="flex items-center gap-2"><SpeakerHigh size={16} weight="bold" className={sel.audio_quality === 'full_commentary' ? 'text-[#06A77D]' : 'text-gray-500'} /><span className={`text-sm font-semibold ${sel.audio_quality === 'full_commentary' ? 'text-[#06A77D]' : 'text-gray-600'}`}>{audioLbl}</span></div>}
                {vibes.length > 0 && (<div><p className="text-xs font-bold text-gray-500 uppercase mb-2">Vibes</p><div className="flex flex-wrap gap-1.5">{vibes.map(t => <span key={t} className="text-xs bg-orange-50 text-[#FF6B35] border border-orange-200 px-2.5 py-1 rounded-full font-semibold">{t}</span>)}</div></div>)}
                {sel.entry_type && <div className="flex items-center gap-2"><Ticket size={16} weight="bold" className="text-gray-500" /><span className={`text-sm font-bold px-3 py-1 rounded-full ${sel.entry_type === 'free_event' || sel.entry_type === 'open_event' ? 'bg-green-100 text-green-700' : sel.entry_type === 'bottle_service' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>{entryLabel(sel.entry_type)}</span></div>}
                {renderTeamLabel(sel.team_specialization) && <div className="bg-gradient-to-r from-blue-50 to-red-50 rounded-lg p-3"><p className="text-sm font-bold text-gray-800">{renderTeamLabel(sel.team_specialization)}</p></div>}
                {sel.fifa_license && sel.fifa_phone && <a href={`tel:${sel.fifa_phone}`} className="flex items-center justify-center gap-2 w-full bg-yellow-50 border border-yellow-300 text-yellow-800 py-3 rounded-lg font-bold text-sm"><ShieldCheck size={16} weight="bold" /> Call: {sel.fifa_phone}</a>}
                <a href={getMapsUrl(sel)} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 w-full bg-[#FF6B35]/10 border border-[#FF6B35]/30 text-[#FF6B35] py-3 rounded-lg font-bold text-sm hover:bg-[#FF6B35] hover:text-white transition-all" data-testid="sheet-directions-btn"><NavigationArrow size={16} weight="bold" /> Get Directions</a>
                {sel.reservation_link && <a href={sel.reservation_link} target="_blank" rel="noopener noreferrer" className="block w-full bg-[#FF6B35] text-white text-center py-3 rounded-lg font-bold text-sm">{sel.entry_type === 'ticketed' ? 'Buy Tickets' : sel.entry_type === 'bottle_service' ? 'Reserve a Table' : 'Make a Reservation'}</a>}
                <button onClick={() => shareEvent(sel)} className="flex items-center justify-center gap-2 w-full bg-gray-100 border border-gray-200 text-gray-700 py-3 rounded-lg font-bold text-sm hover:bg-gray-200 transition-colors" data-testid="sheet-share-btn"><ShareNetwork size={18} weight="bold" /> Share This Event</button>
                <a href="/refer-venue" className="flex items-center justify-center gap-2 w-full text-[#FF6B35] py-2 text-sm font-semibold hover:underline">Know a venue that should be here? Refer them</a>
                <div className="h-4" />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const ListingsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();

  // City/country hub map — slug → display label
  const COUNTRY_SLUGS = { canada: 'Canada', usa: 'USA', mexico: 'Mexico' };
  const CITY_SLUGS = {
    toronto: 'Toronto', vancouver: 'Vancouver', montreal: 'Montreal',
    calgary: 'Calgary', ottawa: 'Ottawa', mississauga: 'Mississauga',
    'new-york': 'New York', miami: 'Miami', 'los-angeles': 'Los Angeles',
    chicago: 'Chicago', houston: 'Houston', dallas: 'Dallas', seattle: 'Seattle',
    boston: 'Boston', philadelphia: 'Philadelphia', atlanta: 'Atlanta',
    'san-francisco': 'San Francisco',
    'mexico-city': 'Mexico City', guadalajara: 'Guadalajara', monterrey: 'Monterrey',
  };

  // Legacy /canada, /usa, /mexico
  const legacyPathCountryMap = { '/canada': 'Canada', '/usa': 'USA', '/mexico': 'Mexico' };
  const pathCountry =
    legacyPathCountryMap[location.pathname] ||
    (params.country ? COUNTRY_SLUGS[params.country.toLowerCase()] || '' : '');
  const pathCity = params.city ? CITY_SLUGS[params.city.toLowerCase()] || params.city.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '';

  // 301-style redirect from old ?country=X query strings to /listings/<country>
  useEffect(() => {
    if (location.pathname !== '/listings') return;
    const q = new URLSearchParams(location.search);
    const qCountry = (q.get('country') || '').toLowerCase();
    if (qCountry && COUNTRY_SLUGS[qCountry]) {
      const qCity = (q.get('city') || '').toLowerCase().replace(/\s+/g, '-');
      const dest = qCity && CITY_SLUGS[qCity]
        ? `/listings/${qCountry}/${qCity}`
        : `/listings/${qCountry}`;
      navigate(dest, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  const [allListings, setAllListings] = useState([]);
  const [displayListings, setDisplayListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMobileLogo, setShowMobileLogo] = useState(false);
  const [logoMinimized, setLogoMinimized] = useState(false);
  const lastScrollY = useRef(0);
  const [filtersOpen, setFiltersOpen] = useState(false);
  // (Msg 778) Live vs Past Events tab on the main /events feed. Past tab
  // refetches with ?past=true to surface the back-catalog (including
  // expired-3d auto-archived cards). Team filter likewise lives at this
  // level so the chip row renders above the feed, not only on the map.
  const [timeTab, setTimeTab] = useState('live');
  const [teamFilter, setTeamFilter] = useState('all');
  const [userLocation, setUserLocation] = useState(null);
  const [locationStatus, setLocationStatus] = useState('idle');
  // IP-based geo (city / region / country / lat / lng) — used to cascade-sort the listings page.
  const [userGeo, setUserGeo] = useState(null);

  // Calculate distance between two lat/lng points in km
  const getDistance = (lat1, lng1, lat2, lng2) => {
    if (!lat1 || !lng1 || !lat2 || !lng2) return null;
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng/2) * Math.sin(dLng/2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  };

  const getDistanceLabel = (listing) => {
    if (!userLocation || !listing.venue_lat || !listing.venue_lng) return null;
    const km = getDistance(userLocation.lat, userLocation.lng, listing.venue_lat, listing.venue_lng);
    if (km === null) return null;
    if (km < 1) return `${Math.round(km * 1000)}m away`;
    if (km < 10) return `${km.toFixed(1)} km away`;
    return `${Math.round(km)} km away`;
  };

  const getMapsUrl = (listing) => {
    if (listing.venue_lat && listing.venue_lng) {
      return `https://www.google.com/maps/dir/?api=1&destination=${listing.venue_lat},${listing.venue_lng}`;
    }
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(listing.location_address)}`;
  };
  const [showMap, setShowMap] = useState(false);
  const [expandedCard, setExpandedCard] = useState(null);
  const [spotlightAds, setSpotlightAds] = useState([]);
  // (Msg 787) Rotate through active campaigns every 8s (see EventListingsPreview).
  const [spotlightIdx, setSpotlightIdx] = useState(0);
  useEffect(() => {
    if (spotlightAds.length < 2) return;
    const t = setInterval(() => setSpotlightIdx(i => (i + 1) % spotlightAds.length), 8000);
    return () => clearInterval(t);
  }, [spotlightAds.length]);
  const cardGridRefs = useRef({});
  const [localHearts, setLocalHearts] = useState({});
  const [flagModal, setFlagModal] = useState(null); // { listing, reason, submitted }
  const [flaggedIds, setFlaggedIds] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('wsp_flagged_listings') || '[]')); }
    catch { return new Set(); }
  });
  const [filters, setFilters] = useState({
    search: '',
    city: pathCity,
    country: pathCountry,
    event_type: '',
    vibes: [],
    entry_type: '',
    audio: '',
    address: ''
  });
  // Optional ?date=YYYY-MM-DD deep-link from Match Schedule page.
  // We never refetch on date — we filter the loaded set client-side because
  // event_date strings are inconsistent (ISO, "Month dd, yyyy", or ranges).
  const [dateFilter, setDateFilter] = useState(() => {
    try { return new URLSearchParams(window.location.search).get('date') || ''; } catch { return ''; }
  });

  // Keep filters in sync when URL pathCountry / pathCity changes (e.g. redirect from ?country=)
  useEffect(() => {
    setFilters(prev => {
      if (prev.country === pathCountry && prev.city === pathCity) return prev;
      const next = { ...prev, country: pathCountry, city: pathCity };
      // Trigger refetch with new path-derived filters
      fetchListings(next);
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathCountry, pathCity]);

  // Dynamic vibe options from actual listings
  const dynamicVibes = useMemo(() => {
    const vibeSet = new Set();
    allListings.forEach(l => {
      if (l.vibe_tags) {
        const tagsArr = Array.isArray(l.vibe_tags) ? l.vibe_tags : String(l.vibe_tags).split(',');
        tagsArr.map(t => (t || '').toString().trim()).filter(Boolean).forEach(v => vibeSet.add(v));
      }
    });
    return [...vibeSet].sort();
  }, [allListings]);

  const seoMeta = useMemo(() => {
    const base = 'https://worldsoccerparty.com';
    let path = '/listings';
    if (pathCountry) path += `/${(params.country || pathCountry).toLowerCase()}`;
    if (pathCity) path += `/${(params.city || pathCity).toLowerCase()}`;
    const canonical = `${base}${path}`;
    if (pathCountry && pathCity) {
      return {
        title: `Soccer Viewing Parties & Fan Events in ${pathCity}, ${pathCountry} | Summer 2026`,
        desc: `Find ${pathCity} soccer 2026 viewing parties, bars, restaurants, fan zones, and live screenings. Updated daily — book your spot near you.`,
        h1: `Events Near Me — ${pathCity}`,
        canonical,
      };
    }
    if (pathCountry) {
      return {
        title: `Soccer Viewing Parties & Fan Events in ${pathCountry} | Summer 2026`,
        desc: `Discover ${pathCountry}'s biggest soccer 2026 viewing parties, fan fests, restaurants, and live screenings. Find bars, patios, and events near you.`,
        h1: `Events Near Me — ${pathCountry}`,
        canonical,
      };
    }
    return {
      title: 'Events Near Me — Soccer 2026 Viewing Parties Across North America',
      desc: 'Discover summer 2026 soccer viewing parties, fan zones, festivals, and live screenings across Canada, USA, and Mexico. Find bars and events near you.',
      h1: 'Events Near Me',
      canonical,
    };
  }, [pathCountry, pathCity, params.country, params.city]);

  // Scroll handler for mobile sticky logo
  useEffect(() => {
    const handleScroll = () => {
      const currentY = window.scrollY;
      const isScrollingDown = currentY > lastScrollY.current;
      if (currentY > 300 && displayListings.length > 0) {
        setShowMobileLogo(true);
        if (isScrollingDown && !logoMinimized) setLogoMinimized(true);
        if (!isScrollingDown && logoMinimized) setLogoMinimized(false);
      } else {
        setShowMobileLogo(false);
      }
      lastScrollY.current = currentY;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [displayListings.length, logoMinimized]);

  const fetchListings = useCallback(async (overrideFilters) => {
    const f = overrideFilters || filters;
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (f.city) params.append('city', f.city);
      if (f.country) params.append('country', f.country);
      if (f.event_type) params.append('event_type', f.event_type);
      if (f.entry_type) params.append('entry_type', f.entry_type);
      if (f.audio) params.append('audio_quality', f.audio);
      if (f.vibes.length > 0) params.append('vibes', f.vibes.join(','));
      params.append('status', 'published');
      // (Msg 778) Past Events tab
      if (timeTab === 'past') params.append('past', 'true');

      const response = await axios.get(`${API}/venues/listings?${params.toString()}`);
      const data = response.data;
      const items = Array.isArray(data) ? data : (data?.items || []);
      setAllListings(items);
      setDisplayListings(items);
    } catch (error) {
      console.error('Error fetching listings:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, timeTab]);

  // (Msg 778) Apply the team-filter chip on top of the API-sorted feed.
  // Matches against both legacy team_specialization AND the new
  // match_coverage_countries field exposed by venue-registration's
  // "Teams We Support" multi-select.
  const visibleListings = useMemo(() => {
    if (teamFilter === 'all') return displayListings;
    return displayListings.filter(l => {
      const fromSpec = parseTeamSpec(l.team_specialization).some(t => t.name === teamFilter);
      if (fromSpec) return true;
      const mcc = l.match_coverage_countries;
      if (Array.isArray(mcc)) {
        return mcc.some(c => {
          const n = normaliseTeam(c);
          return n && n.name === teamFilter;
        });
      }
      return false;
    });
  }, [displayListings, teamFilter]);

  // (Msg 778) Refetch when the Live/Past tab toggles. We intentionally skip
  // the very first render — the mount-time effect below already issues the
  // first fetch using whatever URL params are present.
  const initialTimeTab = useRef(timeTab);
  useEffect(() => {
    if (initialTimeTab.current === timeTab) return;
    fetchListings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeTab]);

  // On mount: read URL country param or path-based country, fetch with it immediately
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const countryParam = params.get('country') || pathCountry;
    if (countryParam) {
      const newFilters = { ...filters, country: countryParam };
      setFilters(newFilters);
      fetchListings(newFilters);
    } else {
      fetchListings();
    }
    // Fetch spotlight ads
    // Featured Spotlight: prefer live paid ads (/ads/serve, geo+schedule-aware),
    // fall back to legacy seeded /ads/spotlight so the slot is never blank.
    (async () => {
      try {
        const live = await axios.get(`${API}/ads/serve?slot=spotlight&limit=3`).then(r => r.data?.items || []).catch(() => []);
        if (live.length) {
          setSpotlightAds(live);
          live.forEach(ad => {
            if (ad.ad_purchase_id) {
              const idx = Number.isFinite(ad.image_index) ? ad.image_index : -1;
              axios.post(`${API}/ads/purchase/${ad.ad_purchase_id}/impression${idx >= 0 ? `?image_index=${idx}` : ''}`).catch(() => {});
            }
          });
          return;
        }
        const seeded = await axios.get(`${API}/ads/spotlight${pathCountry ? `?country=${pathCountry}` : ''}`).then(r => r.data?.items || r.data || []);
        const items = Array.isArray(seeded) ? seeded : [];
        setSpotlightAds(items);
      } catch {}
    })();
    // Fetch IP-based geo for cascade sort (cached per session)
    (async () => {
      try {
        const cached = sessionStorage.getItem('wsp_user_geo_full');
        if (cached) { setUserGeo(JSON.parse(cached)); return; }
        // Use native fetch (not axios) to avoid the CORS preflight that ipapi.co rejects
        const ctrl = new AbortController();
        const t = setTimeout(() => ctrl.abort(), 4000);
        const resp = await fetch('https://ipapi.co/json/', { signal: ctrl.signal });
        clearTimeout(t);
        const d = await resp.json();
        const geo = {
          city: (d?.city || '').trim(),
          region: (d?.region || '').trim(),
          country: (d?.country_name || d?.country || '').trim(),
          lat: typeof d?.latitude === 'number' ? d.latitude : null,
          lng: typeof d?.longitude === 'number' ? d.longitude : null,
        };
        sessionStorage.setItem('wsp_user_geo_full', JSON.stringify(geo));
        setUserGeo(geo);
      } catch {
        setUserGeo({ city: '', region: '', country: '', lat: null, lng: null });
      }
    })();
    // eslint-disable-next-line
  }, []);

  // Deep-link: auto-expand & scroll to shared event
  useEffect(() => {
    if (displayListings.length === 0) return;
    const params = new URLSearchParams(window.location.search);
    const eventId = params.get('event');
    if (eventId) {
      setExpandedCard(eventId);
      setTimeout(() => {
        const el = cardGridRefs.current[eventId];
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 400);
    }
  }, [displayListings]);

  // Cascade-sort listings:
  // Buckets (strict priority):
  //   0 — FIFA Registered + ≤ 15 min walk (≈1.25km) from user (IP or GPS)
  //   1 — FIFA Registered + > 15 min walk (any distance, with geo)
  //   2 — Same city as user
  //   3 — Same province/state/region as user
  //   4 — Same country as user
  //   5 — Remainder
  // Within each bucket: Boosted/storefront-badge first → nearest distance → highest share_count.
  // When NO geo is available we still ensure FIFA Registered listings get a strong score bump
  // so they stay distributed/visible throughout the grid (per user requirement).
  useEffect(() => {
    let filtered = allListings;
    if (filters.search.trim()) {
      const q = filters.search.toLowerCase().trim();
      filtered = allListings.filter(l => {
        const fields = [
          l.event_name, l.venue_name, l.address, l.city,
          l.event_description, l.location_address, l.event_type,
          l.vibe_tags, l.team_specialization, l.event_date
        ].filter(Boolean).join(' ').toLowerCase();
        return fields.includes(q);
      });
    }
    // Date deep-link filter — supports ISO event_date, "Month dd, yyyy", and
    // multi-day textual ranges like "June 11 - July 19, 2026".
    if (dateFilter && /^\d{4}-\d{2}-\d{2}$/.test(dateFilter)) {
      const months = ['january','february','march','april','may','june','july','august','september','october','november','december'];
      const parseDate = (s) => {
        if (!s) return null;
        const raw = String(s).trim();
        const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
        const m = raw.match(/([A-Za-z]+)\s+(\d{1,2})(?:\s*[–-]\s*[A-Za-z]+\s+\d{1,2})?,?\s*(\d{4})/);
        if (m) {
          const mi = months.indexOf(m[1].toLowerCase().slice(0, 9));
          if (mi >= 0) return `${m[3]}-${String(mi + 1).padStart(2, '0')}-${String(parseInt(m[2], 10)).padStart(2, '0')}`;
        }
        return null;
      };
      const parseRange = (l) => {
        const start = parseDate(l.event_date) || parseDate(l.event_dates?.[0]);
        let end = parseDate(l.event_date_end);
        if (!end && typeof l.event_date === 'string') {
          const r = l.event_date.match(/([A-Za-z]+)\s+(\d{1,2})\s*[–-]\s*([A-Za-z]+)\s+(\d{1,2}),?\s*(\d{4})/);
          if (r) {
            const mi = months.indexOf(r[3].toLowerCase().slice(0, 9));
            if (mi >= 0) end = `${r[5]}-${String(mi + 1).padStart(2, '0')}-${String(parseInt(r[4], 10)).padStart(2, '0')}`;
          }
        }
        if (!end) end = start;
        return start ? { start, end } : null;
      };
      filtered = filtered.filter(l => {
        const r = parseRange(l);
        return r && r.start <= dateFilter && dateFilter <= r.end;
      });
    }

    // NEW SORT RULES (Feb 2026 — Msg 704):
    //   1. Pin only the TOP 3 FIFA-licensed events at the very top
    //      (closest distance if geo, else earliest event date).
    //   2. The REST of the listings — including any remaining FIFA events —
    //      drop into a mixed feed sorted by:
    //         a) events happening TODAY (in user's local time) first
    //         b) then by distance from user IP / GPS
    //         c) then by earliest upcoming event date
    //         d) ties broken by share_count
    //   3. When NO geo is available we still surface today's events first,
    //      then fall back to event-date ordering. FIFA stays distributed —
    //      just no longer stacked at the very top after the first three.
    let sorted = filtered;
    try {
      const norm = (s) => (s || '').toString().trim().toLowerCase();
      const refLat = userLocation?.lat ?? userGeo?.lat ?? null;
      const refLng = userLocation?.lng ?? userGeo?.lng ?? null;
      const hasCoords = refLat != null && refLng != null;
      const geoCity = norm(userGeo?.city);
      const geoRegion = norm(userGeo?.region);
      const geoCountry = norm(userGeo?.country);

      // Today key in YYYY-MM-DD (local) so we can match `event_date`.
      const _now = new Date();
      const _tk = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const todayKey = _tk(_now);

      const months = ['january','february','march','april','may','june','july','august','september','october','november','december'];
      const parseStartDate = (s) => {
        if (!s) return null;
        const raw = String(s).trim();
        const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
        const m = raw.match(/([A-Za-z]+)\s+(\d{1,2})(?:\s*[–-]\s*[A-Za-z]+\s+\d{1,2})?,?\s*(\d{4})/);
        if (m) {
          const mi = months.indexOf(m[1].toLowerCase().slice(0, 9));
          if (mi >= 0) return `${m[3]}-${String(mi + 1).padStart(2, '0')}-${String(parseInt(m[2], 10)).padStart(2, '0')}`;
        }
        return null;
      };
      const parseEndDate = (l) => {
        const start = parseStartDate(l.event_date);
        let end = parseStartDate(l.event_date_end);
        if (!end && typeof l.event_date === 'string') {
          const r = l.event_date.match(/([A-Za-z]+)\s+(\d{1,2})\s*[–-]\s*([A-Za-z]+)\s+(\d{1,2}),?\s*(\d{4})/);
          if (r) {
            const mi = months.indexOf(r[3].toLowerCase().slice(0, 9));
            if (mi >= 0) end = `${r[5]}-${String(mi + 1).padStart(2, '0')}-${String(parseInt(r[4], 10)).padStart(2, '0')}`;
          }
        }
        return end || start;
      };
      const isToday = (l) => {
        const start = parseStartDate(l.event_date);
        const end = parseEndDate(l);
        if (!start) return false;
        return start <= todayKey && todayKey <= (end || start);
      };

      const scored = filtered.map(l => {
        const km = hasCoords && l.venue_lat && l.venue_lng
          ? getDistance(refLat, refLng, l.venue_lat, l.venue_lng)
          : null;
        let eventTs = Number.POSITIVE_INFINITY;
        if (l.event_date) {
          const composed = `${l.event_date}${l.event_time ? ' ' + l.event_time : ''}`;
          const parsed = Date.parse(composed);
          if (!Number.isNaN(parsed)) eventTs = parsed;
        }
        const cityMatch = !!geoCity && norm(l.city) === geoCity;
        const regionMatch = !!geoRegion && norm(l.province) === geoRegion;
        const countryMatch = !!geoCountry && norm(l.country) === geoCountry;
        return {
          l,
          km,
          eventTs,
          isFifa: !!l.fifa_license,
          isToday: isToday(l),
          boost: l.addon_storefront_badge ? 1 : 0,
          shares: l.share_count || 0,
          cityMatch, regionMatch, countryMatch,
        };
      });

      // FIFA pin selection — pick the TOP 1 (storefront boost > nearest >
      // earliest). Iter 712 spec calls for 1 FIFA + 2 nearest-to-user on
      // the first row, replacing the prior 3-FIFA pin block.
      const fifaCandidates = scored.filter(s => s.isFifa);
      fifaCandidates.sort((a, b) => {
        if (a.boost !== b.boost) return b.boost - a.boost;
        if (a.km != null && b.km != null && a.km !== b.km) return a.km - b.km;
        if (a.km != null && b.km == null) return -1;
        if (a.km == null && b.km != null) return 1;
        if (a.eventTs !== b.eventTs) return a.eventTs - b.eventTs;
        return b.shares - a.shares;
      });
      const pinnedFifa = fifaCandidates.slice(0, 1);

      // Pick the 2 NEAREST non-FIFA listings as the rest of row 1. If we
      // have no geo (km all null), fall back to the earliest upcoming.
      const fifaInRowSet = new Set(pinnedFifa.map(p => p.l));
      const nonFifaSorted = scored.filter(s => !s.isFifa && !fifaInRowSet.has(s.l));
      nonFifaSorted.sort((a, b) => {
        if (a.boost !== b.boost) return b.boost - a.boost;
        if (a.km != null && b.km != null && a.km !== b.km) return a.km - b.km;
        if (a.km != null && b.km == null) return -1;
        if (a.km == null && b.km != null) return 1;
        if (a.eventTs !== b.eventTs) return a.eventTs - b.eventTs;
        return b.shares - a.shares;
      });
      const nearestTwo = nonFifaSorted.slice(0, 2);
      const pinned = [...pinnedFifa, ...nearestTwo];
      const pinnedSet = new Set(pinned.map(p => p.l));

      // Everything else (including remaining FIFA) — TIME RIPPLE FROM USER:
      //   1) Today's events sorted by distance (closest first)
      //   2) Tomorrow's events sorted by distance
      //   3) Day-after-tomorrow's events sorted by distance
      //   …each new day expands the visible radius naturally because users
      //   scroll deeper to find later dates.
      //   Boosted listings tie-break first within each day, then distance,
      //   then geo cascade (city/region/country), then shares.
      const rest = scored.filter(s => !pinnedSet.has(s.l));
      const startOfToday = new Date(_now.getFullYear(), _now.getMonth(), _now.getDate()).getTime();
      const _msPerDay = 86400000;
      const dayBucket = (s) => {
        if (!Number.isFinite(s.eventTs)) return Number.POSITIVE_INFINITY;
        if (s.eventTs < startOfToday) return -1; // past events sink to bottom
        return Math.floor((s.eventTs - startOfToday) / _msPerDay);
      };
      rest.sort((a, b) => {
        const da = dayBucket(a);
        const db = dayBucket(b);
        // Past events go to the very bottom
        if ((da < 0) !== (db < 0)) return da < 0 ? 1 : -1;
        // Group by event day (today=0, tomorrow=1, …)
        if (da !== db) return da - db;
        // Within the same day — boost wins
        if (a.boost !== b.boost) return b.boost - a.boost;
        // Then distance (closest first)
        if (a.km != null && b.km != null && a.km !== b.km) return a.km - b.km;
        if (a.km != null && b.km == null) return -1;
        if (a.km == null && b.km != null) return 1;
        // Same-city / region / country tiebreakers when distance is unknown
        if (a.cityMatch !== b.cityMatch) return a.cityMatch ? -1 : 1;
        if (a.regionMatch !== b.regionMatch) return a.regionMatch ? -1 : 1;
        if (a.countryMatch !== b.countryMatch) return a.countryMatch ? -1 : 1;
        return b.shares - a.shares;
      });

      sorted = [...pinned.map(p => p.l), ...rest.map(r => r.l)];

      // Dedup pass — the backend expands multi-day events + match-coverage
      // events into one card per day / per country (see server.py:1347+).
      // Every expanded card shares the same parent_listing_id, so collapse
      // them down to ONE card on the public listings grid. We still keep
      // the date-ripple ordering intact: because `sorted` is already in
      // chronological order, the FIRST occurrence we keep is the earliest
      // upcoming day for that event — exactly what the user wants to see.
      // Pinned FIFA cards are immune so the spotlight pins always render.
      const stripSuffix = (s) => norm(s).replace(/\s*[#(]?\s*\d+\s*[)]?\s*$/, '').trim();
      const eventKey = (l) => {
        // Prefer the backend-emitted parent_listing_id (multi-day fan-out).
        if (l.parent_listing_id) return `pid::${l.parent_listing_id}`;
        // Fall back to id for non-expanded rows.
        if (l.id) return `id::${l.id}`;
        // Last resort — composite identity from event name + venue.
        const en = stripSuffix(l.event_name);
        const vn = norm(l.venue_name);
        const cy = norm(l.city);
        if (en || vn) return `comp::${en}::${vn}::${cy}`;
        return `img::${(l.banner_url || '').trim()}::${cy}`;
      };
      const seenKeys = new Set();
      const pinnedSetForDedup = new Set(pinned.map(p => p.l));
      sorted = sorted.filter(l => {
        if (pinnedSetForDedup.has(l)) return true; // FIFA pins always show
        const k = eventKey(l);
        if (!k) return true;
        if (seenKeys.has(k)) return false;
        seenKeys.add(k);
        return true;
      });
    } catch (err) {
      console.error('[ListingsPage] cascade sort failed, falling back to unsorted list:', err);
      sorted = filtered;
    }

    // Safety: only fall back if the sort somehow produced an EMPTY list
    // while there were items to show. We intentionally let the dedup pass
    // shrink the list (multi-day rows collapsing into one card), so the
    // previous "any shorter → fall back" check is too aggressive.
    if (sorted.length === 0 && filtered.length > 0) {
      sorted = filtered;
    }
    setDisplayListings(sorted);
  }, [filters.search, allListings, userLocation, userGeo, dateFilter]);

  const handleSearch = () => fetchListings();
  const clearFilters = () => {
    const empty = { search: '', city: '', country: '', event_type: '', vibes: [], entry_type: '', audio: '', address: '' };
    setFilters(empty);
    fetchListings(empty);
  };

  const toggleVibe = (vibe) => {
    setFilters(prev => ({
      ...prev,
      vibes: prev.vibes.includes(vibe) ? prev.vibes.filter(v => v !== vibe) : [...prev.vibes, vibe]
    }));
  };

  const requestGeolocation = () => {
    setLocationStatus('requesting');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setLocationStatus('granted');
      },
      () => setLocationStatus('denied'),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
    );
  };

  const activeFilterCount = [filters.city, filters.country, filters.event_type, filters.entry_type, filters.audio].filter(Boolean).length + filters.vibes.length + (filters.search ? 1 : 0);

  // JSON-LD ItemList of events — gives Google an event-rich SERP without using trademarked wording
  const itemListSchema = useMemo(() => {
    const items = (displayListings || []).slice(0, 60).map((l, i) => {
      const eventObj = {
        '@type': 'Event',
        name: l.event_name || l.venue_name || 'Soccer Viewing Event',
        eventStatus: 'https://schema.org/EventScheduled',
        eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
        startDate: l.event_date || '2026-06-11',
        location: {
          '@type': 'Place',
          name: l.venue_name || l.city,
          address: l.location_address || [l.city, l.country].filter(Boolean).join(', '),
        },
        url: `https://worldsoccerparty.com/listings?event=${l.id}`,
      };
      if (l.image_url) eventObj.image = l.image_url;
      if (l.event_description) eventObj.description = l.event_description.slice(0, 280);
      return { '@type': 'ListItem', position: i + 1, item: eventObj };
    });
    return {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: seoMeta.h1,
      itemListElement: items,
    };
  }, [displayListings, seoMeta.h1]);

  const breadcrumbSchema = useMemo(() => {
    const crumbs = [{ '@type': 'ListItem', position: 1, name: 'Home', item: 'https://worldsoccerparty.com/' },
                    { '@type': 'ListItem', position: 2, name: 'Events Near Me', item: 'https://worldsoccerparty.com/listings' }];
    if (pathCountry) crumbs.push({ '@type': 'ListItem', position: 3, name: pathCountry, item: `https://worldsoccerparty.com/listings/${(params.country || pathCountry).toLowerCase()}` });
    if (pathCity) crumbs.push({ '@type': 'ListItem', position: 4, name: pathCity, item: seoMeta.canonical });
    return { '@context': 'https://schema.org', '@type': 'BreadcrumbList', itemListElement: crumbs };
  }, [pathCountry, pathCity, params.country, seoMeta.canonical]);

  return (
    <div className="min-h-screen pt-36 md:pt-44 lg:pt-36 pb-24 bg-gray-50">
      <Helmet>
        <title>{seoMeta.title}</title>
        <meta name="description" content={seoMeta.desc} />
        <link rel="canonical" href={seoMeta.canonical} />
        <meta property="og:title" content={seoMeta.title} />
        <meta property="og:description" content={seoMeta.desc} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={seoMeta.canonical} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoMeta.title} />
        <meta name="twitter:description" content={seoMeta.desc} />
        <script type="application/ld+json">{JSON.stringify(itemListSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>
      {/* Mobile Sticky Logo */}
      <AnimatePresence>
        {showMobileLogo && isMobileOrTablet() && (
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="fixed top-14 left-4 z-50 lg:hidden">
            <div className={`bg-white rounded-xl shadow-lg border border-gray-200 flex items-center gap-2 px-3 py-2 transition-all ${logoMinimized ? 'scale-90' : ''}`}>
              <img src="/logo.png" alt="WSP" className="w-8 h-8 rounded-full" onError={(e) => { e.target.style.display='none'; }} />
              <span className={`font-['Bebas_Neue'] text-sm text-gray-800 transition-all ${logoMinimized ? 'hidden' : ''}`}>Events</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <h1 className="font-['Bebas_Neue'] text-4xl sm:text-5xl lg:text-6xl tracking-tight uppercase text-gray-900" data-testid="listings-heading">{seoMeta.h1}</h1>
          <p className="text-gray-500 text-base sm:text-lg mt-2">2026 soccer viewing parties, festivals, and more across North America</p>
        </motion.div>

        {/* Geolocation Prompt */}
        {locationStatus === 'idle' && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-center gap-3" data-testid="geo-prompt">
            <Crosshair size={24} weight="bold" className="text-blue-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-bold text-blue-900">
                {userGeo?.city
                  ? `Showing events near ${userGeo.city}${userGeo.region ? `, ${userGeo.region}` : ''}`
                  : 'Find events near you'}
              </p>
              <p className="text-xs text-blue-700">
                {userGeo?.city
                  ? 'Tap Find Nearby for precise distance sorting (15-min walk priority)'
                  : 'Allow location access to see nearby events and distances'}
              </p>
            </div>
            <button onClick={requestGeolocation} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-xs font-bold" data-testid="geo-allow-btn">Find Nearby</button>
          </motion.div>
        )}
        {locationStatus === 'granted' && (
          <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-2 mb-6 flex items-center gap-2 text-sm text-green-700 font-semibold" data-testid="geo-granted">
            <NavigationArrow size={16} weight="bold" />Showing events near you — distances are live
          </div>
        )}

        {/* Search Bar */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.1 }} className="mb-4">
          <div className="relative">
            <MagnifyingGlass size={20} weight="bold" className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={filters.search}
              onChange={(e) => setFilters({...filters, search: e.target.value})}
              placeholder="Search events, venues, neighborhoods..."
              className="w-full pl-12 pr-12 py-3.5 bg-white border-2 border-gray-200 focus:border-[#FF6B35] rounded-xl text-sm shadow-sm transition-colors"
              data-testid="search-input"
            />
            {filters.search && (
              <button onClick={() => setFilters({...filters, search: ''})} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" data-testid="search-clear">
                <X size={18} weight="bold" />
              </button>
            )}
          </div>
          {filters.search && (
            <p className="text-xs text-gray-500 mt-1.5 ml-1" data-testid="search-result-count">
              {displayListings.length} result{displayListings.length !== 1 ? 's' : ''} for "<span className="font-bold text-gray-700">{filters.search}</span>"
            </p>
          )}
          {dateFilter && /^\d{4}-\d{2}-\d{2}$/.test(dateFilter) && (
            <div className="mt-3 inline-flex items-center gap-2 bg-orange-50 border border-[#FF6B35]/30 text-[#FF6B35] text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full" data-testid="active-date-filter">
              <Calendar size={12} weight="bold" />
              Showing events on {new Date(dateFilter + 'T12:00:00Z').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
              <button
                onClick={() => {
                  setDateFilter('');
                  try {
                    const u = new URL(window.location.href);
                    u.searchParams.delete('date');
                    window.history.replaceState(null, '', u.pathname + (u.search ? u.search : '') + u.hash);
                  } catch { /* URL update is best-effort; in-memory state already cleared */ }
                }}
                className="ml-1 text-[#FF6B35] hover:text-[#e85a24] underline underline-offset-2"
                data-testid="clear-date-filter"
              >Clear</button>
            </div>
          )}
        </motion.div>

        {/* Filter Section */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }} className="bg-white rounded-xl shadow-md border border-gray-100 mb-8 overflow-hidden" data-testid="filter-section">
          <div
            onClick={() => setFiltersOpen(!filtersOpen)}
            className="w-full flex items-center justify-between p-4 sm:p-5 cursor-pointer"
          >
            <div className="flex items-center gap-2">
              <Funnel size={22} weight="bold" className="text-[#FF6B35]" />
              <h2 className="font-['Bebas_Neue'] text-xl sm:text-2xl uppercase text-gray-900">Filter Events</h2>
              {activeFilterCount > 0 && <span className="bg-[#FF6B35] text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">{activeFilterCount}</span>}
            </div>
            <span className="text-gray-400 text-sm font-bold">{filtersOpen ? 'Hide' : 'Show'}</span>
          </div>

          <div className={`px-4 sm:px-5 pb-5 space-y-4 ${!filtersOpen ? 'hidden' : ''}`}>
            {/* Row 1: Country + City + Event Type */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-bold text-gray-600 mb-1 block">Country</label>
                <select value={filters.country} onChange={(e) => setFilters({...filters, country: e.target.value})} className="w-full border-2 border-gray-200 px-3 py-2.5 focus:border-[#FF6B35] rounded-lg text-sm" data-testid="filter-country">
                  <option value="">All Countries</option>
                  <option value="Canada">Canada</option>
                  <option value="USA">USA</option>
                  <option value="Mexico">Mexico</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-bold text-gray-600 mb-1 block">City</label>
                <input type="text" value={filters.city} onChange={(e) => setFilters({...filters, city: e.target.value})} placeholder="e.g., Toronto" className="w-full border-2 border-gray-200 px-3 py-2.5 focus:border-[#FF6B35] rounded-lg text-sm" data-testid="filter-city" />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-600 mb-1 block">Event Type</label>
                <select value={filters.event_type} onChange={(e) => setFilters({...filters, event_type: e.target.value})} className="w-full border-2 border-gray-200 px-3 py-2.5 focus:border-[#FF6B35] rounded-lg text-sm" data-testid="filter-event-type">
                  <option value="">All Types</option>
                  {EVENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>

            {/* Row 2: Entry Type + Audio — aligned side-by-side */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div>
                <label className="text-xs font-bold text-gray-600 mb-1 block">Entry Type</label>
                <div className="flex flex-wrap gap-2">
                  {ENTRY_TYPES.map(et => (
                    <button key={et.value} onClick={() => setFilters({...filters, entry_type: filters.entry_type === et.value ? '' : et.value})} className={`text-xs font-bold px-3 py-1.5 rounded-full border-2 transition-all ${filters.entry_type === et.value ? 'bg-[#FF6B35] text-white border-[#FF6B35]' : 'bg-white text-gray-600 border-gray-200 hover:border-[#FF6B35]'}`} data-testid={`filter-entry-${et.value}`}>
                      {et.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="sm:border-l sm:border-gray-200 sm:pl-4">
                <label className="text-xs font-bold text-gray-600 mb-1 block">Audio</label>
                <div className="flex flex-wrap gap-2">
                  {[{v:'full_commentary',l:'Live Commentary'},{v:'background_music',l:'Music Only'},{v:'silent',l:'Silent'}].map(a => (
                    <button key={a.v} onClick={() => setFilters({...filters, audio: filters.audio === a.v ? '' : a.v})} className={`text-xs font-bold px-3 py-1.5 rounded-full border-2 transition-all ${filters.audio === a.v ? 'bg-[#FF6B35] text-white border-[#FF6B35]' : 'bg-white text-gray-600 border-gray-200 hover:border-[#FF6B35]'}`} data-testid={`filter-audio-${a.v}`}>
                      {a.l}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Row 3: Vibe Tags */}
            <div>
              <label className="text-xs font-bold text-gray-600 mb-2 block">Vibe</label>
              <div className="flex flex-wrap gap-2">
                {dynamicVibes.map(vibe => (
                  <button key={vibe} onClick={() => toggleVibe(vibe)} className={`text-xs font-semibold px-3 py-1.5 rounded-full border-2 transition-all ${filters.vibes.includes(vibe) ? 'bg-[#FF6B35] text-white border-[#FF6B35]' : 'bg-white text-gray-600 border-gray-200 hover:border-[#FF6B35]'}`} data-testid={`filter-vibe-${vibe.replace(/\s/g, '-').toLowerCase()}`}>
                    {vibe}
                  </button>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3 pt-2">
              <button onClick={handleSearch} className="flex-1 sm:flex-none bg-gradient-to-r from-[#FF6B35] to-[#FF8C42] text-white px-8 py-3 font-bold uppercase tracking-wider hover:shadow-lg transition-all flex items-center justify-center gap-2 rounded-lg text-sm" data-testid="filter-apply">
                <MagnifyingGlass size={18} weight="bold" />Search Events
              </button>
              {activeFilterCount > 0 && (
                <button onClick={clearFilters} className="text-gray-500 text-sm font-bold hover:text-[#FF6B35] flex items-center gap-1" data-testid="filter-clear">
                  <X size={16} weight="bold" />Clear All
                </button>
              )}
            </div>
          </div>
        </motion.div>

        {/* (Msg 778) Live/Past tab + Team filter chip rail. Sits between the
            Filter box and the results grid so fans can pivot the feed quickly
            without opening the full filter panel. Visible on every viewport. */}
        {(() => {
          const collectListingTeams = (l) => {
            const out = new Map();
            parseTeamSpec(l.team_specialization).forEach(({ name, flag }) => {
              if (name && !out.has(name)) out.set(name, { label: name, flag });
            });
            const mcc = l.match_coverage_countries;
            if (Array.isArray(mcc)) {
              mcc.forEach(c => {
                const norm = normaliseTeam(c);
                if (norm && norm.name && !out.has(norm.name)) out.set(norm.name, { label: norm.name, flag: norm.flag });
              });
            }
            return [...out.values()];
          };
          const teamOptions = (() => {
            const map = new Map();
            (displayListings || []).forEach(l => collectListingTeams(l).forEach(({ label, flag }) => { if (!map.has(label)) map.set(label, { label, flag }); }));
            return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
          })();
          return (
          <div className="flex items-center gap-2 mb-5 overflow-x-auto pb-2" data-testid="events-time-team-rail" style={{ scrollbarWidth: 'thin' }}>
            <button
              onClick={() => setTimeTab('live')}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-extrabold whitespace-nowrap transition-colors flex-shrink-0 ${timeTab === 'live' ? 'bg-[#10B981] text-white shadow-md' : 'bg-white text-gray-700 border border-gray-300 hover:border-[#10B981]'}`}
              data-testid="time-filter-live"
            >
              <span className="relative flex h-2 w-2">
                <span className={`absolute inline-flex h-full w-full rounded-full ${timeTab === 'live' ? 'bg-white' : 'bg-[#10B981]'} opacity-75 animate-ping`}></span>
                <span className={`relative inline-flex rounded-full h-2 w-2 ${timeTab === 'live' ? 'bg-white' : 'bg-[#10B981]'}`}></span>
              </span>
              Live Events
            </button>
            <button
              onClick={() => setTimeTab('past')}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-extrabold whitespace-nowrap transition-colors flex-shrink-0 ${timeTab === 'past' ? 'bg-[#0B1220] text-white shadow-md' : 'bg-white text-gray-700 border border-gray-300 hover:border-[#0B1220]'}`}
              data-testid="time-filter-past"
            >
              <Clock size={11} weight="bold" />
              Past Events
            </button>
            {teamOptions.length > 0 && (
              <>
                <div className="w-px self-stretch bg-gray-300 mx-1 flex-shrink-0" />
                <button
                  onClick={() => setTeamFilter('all')}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-bold whitespace-nowrap flex-shrink-0 transition-colors ${teamFilter === 'all' ? 'bg-[#FF6B35] text-white shadow-md' : 'bg-white text-gray-700 border border-gray-300 hover:border-[#FF6B35]'}`}
                  data-testid="events-team-filter-all"
                >
                  All Teams
                </button>
                {teamOptions.map(team => {
                  const teamSlug = team.label.replace(/\s+/g, '-').toLowerCase();
                  const active = teamFilter === team.label;
                  return (
                    <button
                      key={team.label}
                      onClick={() => setTeamFilter(team.label)}
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-bold whitespace-nowrap flex-shrink-0 transition-colors ${active ? 'bg-[#FF6B35] text-white shadow-md' : 'bg-white text-gray-700 border border-gray-300 hover:border-[#FF6B35]'}`}
                      data-testid={`events-team-filter-${teamSlug}`}
                    >
                      {team.flag ? <span className="text-base leading-none">{team.flag}</span> : <Star size={11} weight="fill" className={active ? 'text-yellow-300' : 'text-amber-400'} />}
                      {team.label}
                    </button>
                  );
                })}
              </>
            )}
          </div>
          );
        })()}

        {/* Results Bar */}
        {!loading && visibleListings.length > 0 && (
          <div className="flex items-center justify-between mb-6">
            <p className="text-gray-600 text-sm font-semibold" data-testid="results-count">
              {visibleListings.length} event{visibleListings.length !== 1 ? 's' : ''} found
              {timeTab === 'past' && <span className="ml-1 text-gray-500">(past)</span>}
              {filters.country && ` in ${filters.country}`}
              {filters.city && ` near ${filters.city}`}
            </p>
            <Link to="/venue-registration" className="text-[#FF6B35] text-sm font-bold hover:underline">+ List Event Free</Link>
          </div>
        )}

        {/* Listings Grid */}
        {loading ? (
          <div className="text-center text-gray-500 py-12" data-testid="loading-message">Loading events...</div>
        ) : visibleListings.length === 0 ? (
          <div className="text-center bg-white rounded-xl shadow-md p-12 border border-gray-100" data-testid="no-events-message">
            <p className="text-gray-500 text-lg mb-3">No events found matching your criteria.</p>
            <p className="text-gray-400 text-sm mb-6">Try removing some filters to see more results.</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <button onClick={clearFilters} className="text-[#FF6B35] font-bold text-sm underline" data-testid="clear-filters-empty">Clear All Filters</button>
              <Link to="/venue-registration" className="bg-gradient-to-r from-[#FF6B35] to-[#FF8C42] text-white px-8 py-4 font-bold uppercase tracking-wider hover:shadow-lg transition-all inline-block rounded-lg" data-testid="list-venue-cta">List Your Venue</Link>
            </div>
          </div>
        ) : (
          <>
          {/* Featured Spotlight Ad */}
          {spotlightAds.length > 0 && (() => {
            const ad = spotlightAds[spotlightIdx % spotlightAds.length];
            const isLive = !!ad.ad_purchase_id;
            const trackClick = () => {
              if (isLive) {
                const idx = Number.isFinite(ad.image_index) ? ad.image_index : -1;
                axios.post(`${API}/ads/purchase/${ad.ad_purchase_id}/click${idx >= 0 ? `?image_index=${idx}` : ''}`).catch(() => {});
              }
              else if (ad.id) axios.post(`${API}/ads/${ad.id}/click`).catch(() => {});
            };
            const dest = ad.cta_link || '#';
            const isExternal = /^https?:\/\//i.test(dest);
            const LinkOrA = isExternal
              ? ({ children, ...rest }) => <a href={dest} target="_blank" rel="noopener sponsored" {...rest}>{children}</a>
              : ({ children, ...rest }) => <Link to={dest} {...rest}>{children}</Link>;
            if (isLive && ad.banner_url) {
              return (
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-6" data-testid="featured-spotlight">
                  <div className="flex items-center gap-2 mb-2 px-1">
                    <span className="inline-flex items-center text-[10px] font-black uppercase tracking-widest text-gray-500 bg-gray-100 border border-gray-200 px-2 py-1 rounded-full" data-testid="featured-spotlight-sponsored-label">Sponsored</span>
                    {ad.company && (
                      <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">· {ad.company}</span>
                    )}
                  </div>
                  <LinkOrA onClick={trackClick} className="block relative rounded-xl overflow-hidden border-2 border-[#FF6B35] transition-all group" data-testid="featured-spotlight-live">
                    <img src={ad.banner_url} alt={ad.company || 'Sponsored'} className="w-full h-auto object-cover" loading="lazy" />
                  </LinkOrA>
                </motion.div>
              );
            }
            return (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-6" data-testid="featured-spotlight">
              <Link to={ad.cta_link} onClick={trackClick} className="block relative rounded-xl overflow-hidden border-2 border-[#FF6B35] transition-all group">
                <div className="bg-gradient-to-r from-[#FF6B35]/5 via-white to-[#FF6B35]/5 p-6 sm:p-8">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
                    <div className="w-16 h-16 bg-gradient-to-br from-[#FF6B35] to-[#FF8C42] rounded-xl flex items-center justify-center flex-shrink-0 shadow-md">
                      <Megaphone size={28} weight="bold" className="text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] font-black uppercase tracking-widest text-[#FF6B35] bg-[#FF6B35]/10 px-2 py-0.5 rounded-full">Sponsored</span>
                        <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Featured Spotlight</span>
                      </div>
                      <h3 className="font-['Bebas_Neue'] text-2xl sm:text-3xl uppercase text-gray-900 mb-1">{ad.headline}</h3>
                      <p className="text-gray-500 text-sm">{ad.description}</p>
                    </div>
                    <span className="inline-flex items-center gap-1 bg-gradient-to-r from-[#FF6B35] to-[#FF8C42] text-white px-6 py-3 rounded-lg font-bold text-sm uppercase group-hover:shadow-lg transition-all flex-shrink-0">
                      {ad.cta_text} <ArrowRight size={16} weight="bold" />
                    </span>
                  </div>
                </div>
                <div className="absolute inset-0 rounded-xl pointer-events-none" />
              </Link>
            </motion.div>
            );
          })()}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {visibleListings.map((listing, index) => {
              const cardKey = listing.virtual_id || listing.id;
              const isExpanded = expandedCard === cardKey;
              const vibeTags = listing.vibe_tags
                ? (Array.isArray(listing.vibe_tags) ? listing.vibe_tags : String(listing.vibe_tags).split(',')).map(t => (t || '').toString().trim()).filter(Boolean)
                : [];
              const audioLabel = listing.audio_quality === 'full_commentary' ? 'Live Commentary' :
                listing.audio_quality === 'background_music' ? 'Music Only' : listing.audio_quality === 'silent' ? 'Silent/Subtitles' : '';

              return (
              <motion.div
                key={cardKey}
                ref={el => cardGridRefs.current[cardKey] = el}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.04 }}
                className={`bg-white rounded-xl overflow-hidden shadow-md transition-all border cursor-pointer ${listing.fifa_license ? 'border-yellow-400 ring-1 ring-yellow-300' : 'border-gray-100'} ${isExpanded ? 'shadow-xl ring-2 ring-[#FF6B35]/30' : 'hover:shadow-xl hover:-translate-y-1'}`}
                data-testid={`event-card-${index}`}
                onClick={() => setExpandedCard(isExpanded ? null : cardKey)}
              >
                <div className="relative">
                  {listing.image_url ? (
                    <img src={listing.image_url} alt={listing.event_name} className="w-full h-48 object-cover" />
                  ) : (
                    <div className="w-full h-48 bg-gradient-to-br from-[#FF6B35] to-[#FF8C42] flex items-center justify-center">
                      <span className="text-white font-bold text-lg opacity-50">{listing.event_type}</span>
                    </div>
                  )}
                  {listing.fifa_license && (
                    <div className="absolute top-3 right-3 bg-blue-500 text-white text-xs px-2 py-1 rounded-full font-bold flex items-center gap-1">
                      <ShieldCheck size={12} weight="bold" />VERIFIED
                    </div>
                  )}
                  <div className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm text-xs px-3 py-1 rounded-full font-bold text-gray-800">{listing.event_type}</div>
                  {isPlaceholderListing(listing) && getDaysToKickoff() > 0 && (
                    <div
                      className="absolute top-12 left-3 flex items-center gap-1 bg-amber-500 text-white text-[10px] px-2 py-1 rounded-full font-bold shadow-md ring-2 ring-amber-200/70"
                      title="This venue slot has not been claimed yet. World Soccer 2026 kicks off June 11, 2026."
                      data-testid={`placeholder-countdown-${index}`}
                    >
                      <Clock size={10} weight="bold" />
                      <span>GOES LIVE IN {getDaysToKickoff()}D</span>
                    </div>
                  )}
                  {listing.pricing_package === 'premium' && (
                    <div className="absolute bottom-3 left-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs px-2 py-1 rounded-full font-bold">PREMIUM</div>
                  )}
                  {/* Heart Count + Share Icon + Bug Flag */}
                  <div className="absolute bottom-3 right-3 flex items-center gap-1.5">
                    <button onClick={(e) => { e.stopPropagation(); heartEvent(listing, (c) => setLocalHearts(p => ({...p, [listing.id]: c}))); }} className="flex items-center gap-1 bg-black/60 backdrop-blur-sm text-white px-2.5 py-1.5 rounded-full text-xs font-bold hover:bg-black/80 transition-colors" data-testid={`heart-counter-${index}`}>
                      <Heart size={14} weight="fill" className="text-red-400" />
                      <span>{(localHearts[listing.id] ?? listing.heart_count ?? 0).toLocaleString()}</span>
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); shareEvent(listing, (c) => setLocalHearts(p => ({...p, [listing.id]: c}))); }} className="flex items-center bg-black/60 backdrop-blur-sm text-white p-1.5 rounded-full hover:bg-black/80 transition-colors" data-testid={`share-icon-${index}`}>
                      <ShareNetwork size={14} weight="bold" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setFlagModal({ listing, reason: null, submitted: false }); }}
                      disabled={flaggedIds.has(listing.id)}
                      title={flaggedIds.has(listing.id) ? 'You already flagged this listing — thanks!' : 'Report an issue with this listing'}
                      className={`flex items-center backdrop-blur-sm p-1.5 rounded-full transition-colors ${flaggedIds.has(listing.id) ? 'bg-emerald-500/80 text-white cursor-default' : 'bg-black/60 text-white hover:bg-amber-500/90'}`}
                      data-testid={`flag-listing-${index}`}
                    >
                      {flaggedIds.has(listing.id) ? <CheckCircle size={14} weight="bold" /> : <Flag size={14} weight="bold" />}
                    </button>
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="font-['Bebas_Neue'] text-xl sm:text-2xl uppercase text-gray-900 mb-1">{listing.event_name}</h3>
                  {listing.match_label && (
                    <div className="inline-flex items-center gap-1.5 bg-gradient-to-r from-orange-100 to-amber-100 text-orange-800 border border-orange-200 px-2.5 py-0.5 rounded-full text-[11px] font-bold mb-2" data-testid={`match-coverage-badge-${index}`}>
                      <span className="text-base leading-none">⚽</span>
                      <span>{listing.match_label}</span>
                    </div>
                  )}
                  <p className="text-gray-600 text-sm mb-3 line-clamp-2">{listing.event_description}</p>
                  
                  <div className="space-y-1.5 mb-3">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <MapPin size={14} weight="bold" className="text-[#FF6B35] flex-shrink-0" />
                      <span className="truncate">{listing.city || listing.location_address}, {listing.country}</span>
                    </div>
                    {getDistanceLabel(listing) && (
                      <div className="flex items-center gap-2 text-sm text-green-600 font-semibold">
                        <NavigationArrow size={14} weight="bold" className="flex-shrink-0" />
                        <span>{getDistanceLabel(listing)}</span>
                      </div>
                    )}
                    {listing.event_date && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Calendar size={14} weight="bold" className="text-[#FF6B35] flex-shrink-0" />
                        <span>{listing.event_date}</span>
                      </div>
                    )}
                    {listing.event_time && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Clock size={14} weight="bold" className="text-[#FF6B35] flex-shrink-0" />
                        <span>{listing.event_time}</span>
                      </div>
                    )}
                    {listing.audio_quality === 'full_commentary' && (
                      <div className="flex items-center gap-2 text-sm text-[#06A77D]">
                        <SpeakerHigh size={14} weight="bold" />
                        <span className="font-semibold">Live Commentary</span>
                      </div>
                    )}
                  </div>

                  {listing.vibe_tags && (() => {
                    const tagsArr = Array.isArray(listing.vibe_tags) ? listing.vibe_tags : String(listing.vibe_tags).split(',');
                    const clean = tagsArr.map(t => (t || '').toString().trim()).filter(Boolean);
                    if (!clean.length) return null;
                    return (
                      <div className="flex flex-wrap gap-1 mb-3">
                        {clean.slice(0, 3).map(tag => (
                          <span key={tag} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{tag}</span>
                        ))}
                        {clean.length > 3 && (
                          <span className="text-xs text-gray-400 px-1">+{clean.length - 3}</span>
                        )}
                      </div>
                    );
                  })()}

                  {listing.fifa_license && listing.fifa_phone && (
                    <div className="flex items-center gap-2 text-xs font-bold text-yellow-700 bg-yellow-50 border border-yellow-300 rounded-lg px-3 py-2 mb-3">
                      <MapPin size={12} weight="bold" />
                      <span>{listing.fifa_phone}</span>
                    </div>
                  )}

                  <div className="border-t border-gray-100 pt-3 flex items-center justify-between">
                    <p className="text-sm font-bold text-[#FF6B35] uppercase tracking-wider truncate mr-2">{listing.venue_name}</p>
                    {listing.entry_type && (
                      <span className={`text-xs font-bold px-2 py-1 rounded-full flex-shrink-0 ${
                        listing.entry_type === 'free_event' || listing.entry_type === 'open_event' ? 'bg-green-100 text-green-700' :
                        listing.entry_type === 'bottle_service' ? 'bg-purple-100 text-purple-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {listing.entry_type === 'free_event' ? 'FREE' :
                         listing.entry_type === 'open_event' ? 'OPEN' :
                         listing.entry_type === 'ticketed' ? 'TICKETS' :
                         listing.entry_type === 'bottle_service' ? 'VIP' :
                         listing.entry_type === 'no_cover' ? 'NO COVER' :
                         listing.entry_type.replace('_', ' ').toUpperCase()}
                      </span>
                    )}
                  </div>

                  {/* Tap to expand hint */}
                  <div className={`text-center mt-3 text-xs font-semibold transition-colors ${isExpanded ? 'text-[#FF6B35]' : 'text-gray-400'}`}>
                    {isExpanded ? 'Tap to collapse' : 'Tap for more details'}
                  </div>
                </div>

                {/* Expanded Detail Panel */}
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                      data-testid={`event-card-expanded-${index}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="px-5 pb-5 border-t border-gray-100 pt-4 space-y-4">
                        {/* Full description */}
                        {listing.event_description && (
                          <div>
                            <p className="text-xs font-bold text-gray-500 uppercase mb-1">About</p>
                            <p className="text-sm text-gray-700">{listing.event_description}</p>
                          </div>
                        )}

                        {/* Map + Address */}
                        {listing.location_address && (
                          <div>
                            {listing.venue_lat && listing.venue_lng && (
                              listing.addon_map_link ? (
                                <a href={getMapsUrl(listing)} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="block mb-2" data-testid="venue-static-map">
                                  <img src={getStaticMapUrl(listing.venue_lat, listing.venue_lng, 15, 400, 150)} alt={`Map of ${listing.venue_name}`} className="w-full h-32 object-cover rounded-lg border-2 border-[#FF6B35]" loading="lazy" />
                                </a>
                              ) : (
                                <div className="mb-2">
                                  <img src={getStaticMapUrl(listing.venue_lat, listing.venue_lng, 15, 400, 150)} alt={`Map of ${listing.venue_name}`} className="w-full h-32 object-cover rounded-lg border border-gray-200" loading="lazy" />
                                </div>
                              )
                            )}
                            <p className="text-sm text-gray-700 flex items-center gap-1.5">
                              <MapPin size={14} weight="bold" className="text-[#FF6B35]" /> {listing.location_address}
                            </p>
                            {getDistanceLabel(listing) && (
                              <p className="text-xs text-green-600 font-bold mt-1 flex items-center gap-1">
                                <NavigationArrow size={12} weight="bold" /> {getDistanceLabel(listing)}
                              </p>
                            )}
                          </div>
                        )}
                        {listing.addon_map_link && listing.location_address && (
                          <a href={getMapsUrl(listing)} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
                            className="flex items-center justify-center gap-2 w-full bg-[#FF6B35]/10 border border-[#FF6B35]/30 text-[#FF6B35] py-2.5 rounded-lg font-bold text-sm hover:bg-[#FF6B35] hover:text-white transition-all"
                            data-testid="get-directions-btn">
                            <NavigationArrow size={16} weight="bold" /> Get Directions
                          </a>
                        )}
                        {listing.addon_buy_round && (
                          <a href={listing.menu_url || listing.addon_buy_round_menu_url || '#'} target="_blank" rel="noopener noreferrer" onClick={(e) => { e.stopPropagation(); if (!listing.menu_url && !listing.addon_buy_round_menu_url) e.preventDefault(); }}
                            className="flex items-center justify-center gap-2 w-full bg-amber-500 text-white py-2.5 rounded-lg font-bold text-sm hover:bg-amber-600 transition-all"
                            data-testid="view-event-menu-btn">
                            View Event Menu
                          </a>
                        )}

                        {/* Audio quality */}
                        {audioLabel && (
                          <div>
                            <p className="text-xs font-bold text-gray-500 uppercase mb-1">Audio</p>
                            <div className="flex items-center gap-2">
                              <SpeakerHigh size={14} weight="bold" className={listing.audio_quality === 'full_commentary' ? 'text-[#06A77D]' : 'text-gray-500'} />
                              <span className="text-sm text-gray-700">{audioLabel}</span>
                            </div>
                          </div>
                        )}

                        {/* All Vibe Tags */}
                        {vibeTags.length > 0 && (
                          <div>
                            <p className="text-xs font-bold text-gray-500 uppercase mb-2">Vibes</p>
                            <div className="flex flex-wrap gap-1.5">
                              {vibeTags.map(tag => (
                                <span key={tag} className="text-xs bg-orange-50 text-[#FF6B35] border border-orange-200 px-2.5 py-1 rounded-full font-semibold">{tag}</span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Team specialization */}
                        {renderTeamLabel(listing.team_specialization) && (
                          <div className="bg-gradient-to-r from-blue-50 to-red-50 rounded-lg p-3">
                            <p className="text-xs font-bold text-gray-500 uppercase mb-1">Team</p>
                            <p className="text-sm font-bold text-gray-800">{renderTeamLabel(listing.team_specialization)}</p>
                          </div>
                        )}

                        {/* Screens info */}
                        {listing.screens && (() => {
                          try {
                            const screens = typeof listing.screens === 'string' ? JSON.parse(listing.screens) : listing.screens;
                            if (screens && screens.length > 0 && screens.some(s => s.description)) {
                              return (
                                <div>
                                  <p className="text-xs font-bold text-gray-500 uppercase mb-2">Viewing Areas</p>
                                  <div className="space-y-1">
                                    {screens.filter(s => s.description).map((s, si) => (
                                      <div key={si} className="flex items-center gap-2 text-sm text-gray-700">
                                        <Television size={14} weight="bold" className="text-gray-400" />
                                        <span>{s.description} {s.capacity && `(${s.capacity} seats)`} — {s.level}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              );
                            }
                          } catch { return null; }
                          return null;
                        })()}

                        {/* Website + Socials */}
                        <div className="flex flex-wrap gap-3">
                          {listing.website_link && (
                            <a href={listing.website_link} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 font-semibold hover:underline flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                              <Globe size={12} weight="bold" />Website
                            </a>
                          )}
                          {listing.social_links && (() => {
                            try {
                              const socials = typeof listing.social_links === 'string' ? JSON.parse(listing.social_links) : listing.social_links;
                              if (socials && socials.length > 0) {
                                return socials.filter(s => s.url).map((s, si) => (
                                  <a key={si} href={s.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 font-semibold hover:underline" onClick={(e) => e.stopPropagation()}>
                                    {s.platform}
                                  </a>
                                ));
                              }
                            } catch { return null; }
                            return null;
                          })()}
                        </div>

                        {/* Reservation / Ticket link */}
                        {listing.reservation_link && (
                          <a href={listing.reservation_link} target="_blank" rel="noopener noreferrer" className="block w-full bg-[#FF6B35] text-white text-center py-3 rounded-lg font-bold text-sm hover:bg-[#FF8C42] transition-colors" onClick={(e) => e.stopPropagation()}>
                            {listing.entry_type === 'ticketed' ? 'Buy Tickets' :
                             listing.entry_type === 'bottle_service' ? 'Reserve a Table' :
                             'Make a Reservation'}
                          </a>
                        )}

                        {/* Verified phone */}
                        {listing.fifa_license && listing.fifa_phone && (
                          <a href={`tel:${listing.fifa_phone}`} className="flex items-center justify-center gap-2 w-full bg-yellow-50 border border-yellow-300 text-yellow-800 py-2.5 rounded-lg font-bold text-sm" onClick={(e) => e.stopPropagation()}>
                            <ShieldCheck size={14} weight="bold" /> Call Venue: {listing.fifa_phone}
                          </a>
                        )}

                        {/* Share Button */}
                        <button onClick={(e) => { e.stopPropagation(); shareEvent(listing); }} className="flex items-center justify-center gap-2 w-full bg-gray-100 border border-gray-200 text-gray-700 py-2.5 rounded-lg font-bold text-sm hover:bg-gray-200 transition-colors" data-testid={`share-btn-${index}`}>
                          <ShareNetwork size={16} weight="bold" /> Share This Event
                        </button>

                        {/* Claim Venue — hidden for internal World Soccer events */}
                        {!(listing.event_name || '').startsWith('World Soccer') && (
                        <>
                        {isPlaceholderListing(listing) && getDaysToKickoff() > 0 && (
                          <div className="rounded-lg border-2 border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50 px-4 py-3 flex items-center gap-3" data-testid={`placeholder-countdown-banner-${index}`}>
                            <div className="flex-shrink-0 bg-amber-500 text-white rounded-full w-10 h-10 flex items-center justify-center">
                              <Clock size={18} weight="bold" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-bold text-amber-700 uppercase tracking-wide">Unclaimed Venue Slot</p>
                              <p className="text-sm font-bold text-gray-900">Goes live in <span className="text-amber-600">{getDaysToKickoff()} days</span> — World Soccer 2026 kickoff</p>
                            </div>
                          </div>
                        )}
                        {/* (Msg 779) Hide the "Claim This Venue" CTA on past
                            events — venue claiming for an event that already
                            ended is nonsense, and showing it on the Past
                            Events tab dilutes the archive's social-proof feel. */}
                        {timeTab !== 'past' && (
                          <Link to={`/venue-registration?claim=${listing.id}`} onClick={(e) => e.stopPropagation()} className="flex items-center justify-center gap-2 w-full bg-[#FF6B35] text-white py-2.5 rounded-lg font-bold text-sm hover:bg-[#FF8C42] transition-colors" data-testid={`claim-venue-${index}`}>
                            <ShieldCheck size={16} weight="bold" /> Claim This Venue
                          </Link>
                        )}
                        </>
                        )}

                        {/* Refer a Venue */}
                        <Link to="/refer-venue" onClick={(e) => e.stopPropagation()} className="flex items-center justify-center gap-2 w-full text-[#FF6B35] py-2 text-sm font-semibold hover:underline" data-testid={`refer-venue-${index}`}>
                          Know a venue that should be here? Refer them
                        </Link>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
              );
            })}
          </div>
          </>
        )}
      </div>

      {/* Newsletter prompt */}
      <div className="max-w-2xl mx-auto mt-8 mb-4 px-4">
        <NewsletterInline
          hook="Don't miss a 'Last Call' alert. Get notified when your favourite venue has open tables."
          buttonText="Notify Me"
          testId="listings-newsletter"
          source="Listings Fan"
          className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm"
        />
      </div>

      {/* Mobile/Tablet Map FAB — only shows on <lg */}
      {!loading && displayListings.length > 0 && (
        <button
          onClick={() => setShowMap(true)}
          className="lg:hidden fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-gray-900 text-white px-6 py-3 rounded-full shadow-xl flex items-center gap-2 font-bold text-sm active:scale-95 transition-transform"
          data-testid="map-fab-btn"
        >
          <MapTrifold size={20} weight="bold" />
          Map
        </button>
      )}

      {/* Mobile Map Overlay */}
      <AnimatePresence>
        {showMap && (
          <MobileMapOverlay
            listings={displayListings}
            onClose={() => setShowMap(false)}
            userLocation={userLocation}
          />
        )}
      </AnimatePresence>

      {/* Flag-a-Bug Modal */}
      {flagModal && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4" data-testid="flag-modal">
          <div className="absolute inset-0 bg-black/60" onClick={() => setFlagModal(null)} />
          <div className="relative bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden">
            <div className="bg-gray-900 text-white px-5 py-4 flex items-center justify-between">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-wider text-amber-400">Report an issue</p>
                <p className="font-bold text-lg truncate" data-testid="flag-modal-title">{flagModal.listing.event_name}</p>
              </div>
              <button onClick={() => setFlagModal(null)} className="w-9 h-9 rounded-lg bg-gray-800 hover:bg-gray-700 flex items-center justify-center" data-testid="flag-modal-close"><X size={18} weight="bold" /></button>
            </div>
            {flagModal.submitted ? (
              <div className="p-6 text-center" data-testid="flag-modal-success">
                <CheckCircle size={48} weight="fill" className="text-emerald-500 mx-auto mb-3" />
                <p className="font-bold text-gray-900 mb-1">Thanks for the heads up!</p>
                <p className="text-sm text-gray-500 mb-5">Our admins will review and update this listing shortly.</p>
                <button onClick={() => setFlagModal(null)} className="bg-gray-900 text-white px-5 py-2.5 rounded-lg font-bold text-sm" data-testid="flag-modal-done">Done</button>
              </div>
            ) : (
              <div className="p-5 space-y-2">
                <p className="text-sm text-gray-600 mb-2">What needs fixing? Pick one:</p>
                {[
                  { key: 'cover_image', label: 'Cover Image', sub: 'Wrong, blurry, or generic photo' },
                  { key: 'details', label: 'Details', sub: 'Name, description, or contact info is off' },
                  { key: 'dates', label: 'Dates & Times', sub: 'Event date or hours are incorrect' },
                  { key: 'location', label: 'Location', sub: 'Address or map pin is wrong' },
                  { key: 'price', label: 'Price', sub: 'Cover charge or pricing is outdated' },
                ].map(opt => (
                  <button
                    key={opt.key}
                    onClick={async () => {
                      try {
                        await axios.post(`${API}/venues/${flagModal.listing.id}/flag`, { reason: opt.key });
                        const next = new Set(flaggedIds); next.add(flagModal.listing.id);
                        setFlaggedIds(next);
                        try { localStorage.setItem('wsp_flagged_listings', JSON.stringify([...next])); } catch {}
                        setFlagModal({ ...flagModal, submitted: true });
                      } catch (e) {
                        alert('Could not submit flag — please try again.');
                      }
                    }}
                    className="w-full text-left bg-gray-50 hover:bg-amber-50 hover:border-amber-300 border-2 border-transparent rounded-xl px-4 py-3 transition-colors group"
                    data-testid={`flag-reason-${opt.key}`}
                  >
                    <p className="font-bold text-gray-900 text-sm group-hover:text-amber-700">{opt.label}</p>
                    <p className="text-xs text-gray-500">{opt.sub}</p>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ListingsPage;
